import React, { RefObject, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

import SearchIcon from '../../../img/search-icon.svg';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';
import PersonIcon from '../../../img/person.svg';
import FilterIcon from '../../../img/filter.svg';
import CleanIcon from '../../../img/cross-circle.svg';
import SaveIcon from '../../../img/save.svg';
import CarWhite from '../../../img/car-white.svg';

import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { useTableColumns } from '../../../hooks/useTableColumns';
import { ESTEIRA } from '../../../shared/esteiras';
import { fetchCollaborators, fetchUsersByTypes, getCarsByCollaborator, searchCollaborators } from '../../../api/collaboratorsApi';
import { updateCarStatus, getCarStatusHistory } from '../../../api/carsApi';
import { getUserById } from '../../../api/usersApi';
import { verifyPassword } from '../../../api/authApi';
import { getAtividadesCarro } from '../../../api/atividadeCarroApi';
import { buscarServicosPendentes, mensagemConfirmacaoEntrega } from '../../../utils/servicosPendentes';
import { ServicoInacabado } from '../../../interfaces/AtividadeCarro';
import { exportWorkSessionsCsv, getMyRejections, acknowledgeRejection, getAtendimentos, AtendimentoResumo } from '../../../api/workSessionsApi';
import { markNotificationAsRead } from '../../../api/notificationsApi';
import { notify } from '../../../shared/feedback/notify';
import { usePausaAutomaticaAtiva } from '../../../hooks/usePausaAutomaticaAtiva';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
import {
  usePauseWorkSessionMutation,
  useResumeWorkSessionMutation,
  useStartWorkSessionMutation,
  useStopWorkSessionMutation,
  useWorkSessionsQuery,
} from '../../../infrastructure/react-query/hooks/useWorkSessionsQueries';
import { WorkSession, WorkSessionApprovalStatus, WorkSessionExportFilters, WorkSessionStatus } from '../../../interfaces/WorkSession';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import AtendimentoDetailsModal from '../../../components/modal/atendimento-details/AtendimentoDetailsModal';
import SubtarefasDoVeiculo from '../components/SubtarefasDoVeiculo';
import FaixaDeParalelas from '../components/FaixaDeParalelas';

import './ControleProducao.css';
import '../../recepcao/pages/Recepcao.css';
import '../../home/pages/home.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema
import { lerTokenDoCookie } from '../../../shared/cookie-da-sessao';
import MotivoDaParada, { pendenciaDoMotivo, useMotivosDaParada } from '../../../components/motivo-da-parada/MotivoDaParada';

interface ControleProducaoProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

  interface DecodedToken {
  sub: string;
  userType: 'RECEPCAO' | 'CHEFE_DE_OFICINA' | 'ORCAMENTO' | 'ESTOQUE' | 'COMPRA' | 'COLABORADOR' | 'CONSULTOR' | 'REGIONAL' | 'ADMIN';
  isAdmin: boolean;
  username?: string;
}

const IMPLICIT_STATUSES = ['AGUARDANDO', 'AGUARDANDO_PROXIMA_ETAPA', 'AGUARDANDO_ENTREGA', 'ENTREGA'];

const formatDuration = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const modalInputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #d1d5db',
  borderRadius: '10px',
  padding: '10px 14px',
  fontSize: '14px',
  color: '#1f2937',
  backgroundColor: '#f9fafb',
  fontFamily: 'AmpleSoft-Regular, Roboto, sans-serif',
  outline: 'none',
};

const ControleProducao: React.FC<ControleProducaoProps> = () => {
  const { esteirasAtivas, labelDe, chaveDe } = useEsteiras();
  const activeLanesIds = useMemo(() => esteirasAtivas.map((e) => e.id).join(','), [esteirasAtivas]);
  const [userId, setUserId] = useState<string>('');
  const [userType, setUserType] = useState<DecodedToken['userType'] | null>(null);
  const [userDisplayName, setUserDisplayName] = useState<string>('');

  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [collaboratorIdFilter, setCollaboratorIdFilter] = useState('');
  const [esteiraFilter, setEsteiraFilter] = useState('');
  const [entryStartFilter, setEntryStartFilter] = useState('');
  const [entryEndFilter, setEntryEndFilter] = useState('');
  const [hasPendingFilter, setHasPendingFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Listagem agrupada por atendimento (uma linha por entrada do veículo na oficina).
  const [atendimentos, setAtendimentos] = useState<AtendimentoResumo[]>([]);
  const [atendimentosTotal, setAtendimentosTotal] = useState(0);
  const [loadingAtendimentos, setLoadingAtendimentos] = useState(true);
  const [atendimentosRefresh, setAtendimentosRefresh] = useState(0);
  const [selectedAtendimentoCarId, setSelectedAtendimentoCarId] = useState<string | null>(null);

  const [collaborators, setCollaborators] = useState<Array<{ id: string; fullName?: string; username?: string }>>([]);
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);
  const [totalCollaboratorsCount, setTotalCollaboratorsCount] = useState(0);
  const [myCars, setMyCars] = useState<any[]>([]);
  const [myCarsRefreshTrigger, setMyCarsRefreshTrigger] = useState(0);
  const [confirmUnrealizedModal, setConfirmUnrealizedModal] = useState<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    message: '',
    onConfirm: () => { },
  });

  const isChefeView = userType === 'CHEFE_DE_OFICINA' || userType === 'ADMIN';
  const isCollaboratorView = userType === 'COLABORADOR';

  // at-48b: durante uma janela de pausa automática (almoço, lanche) o colaborador não deve
  // iniciar/pausar/retomar/finalizar atividade. O hook liga e desliga sozinho no horário
  // (timer na borda exata, relógio do servidor). Só o COLABORADOR é travado.
  // at-53: o mesmo bloqueio vale fora do expediente (oficina fechada ou fora do horário).
  const { janelaAtiva, foraDoExpediente } = usePausaAutomaticaAtiva(isCollaboratorView);
  const acoesBloqueadasPorPausa = isCollaboratorView && (!!janelaAtiva || !!foraDoExpediente);

  const rotuloJanela = janelaAtiva
    ? `${janelaAtiva.nome} (${janelaAtiva.horaInicio} às ${janelaAtiva.horaFim})`
    : '';

  /** Tooltip dos botões desabilitados — diz QUAL das duas razões está em vigor. */
  const tooltipBloqueio = janelaAtiva
    ? `Bloqueado durante a pausa automática (${rotuloJanela})`
    : foraDoExpediente
      ? `Bloqueado fora do expediente. ${foraDoExpediente.descricao}`
      : undefined;

  /**
   * Guarda dos handlers. Existe além do `disabled` dos botões porque há a borda real de quem
   * abriu o modal às 11:59 e confirmou às 12:01 — o botão do card já estava renderizado.
   *
   * A janela tem precedência na mensagem: quando as duas condições valem (almoço configurado fora
   * do expediente), "estamos no horário de almoço" é o que o colaborador entende.
   */
  const bloqueadoPorJanela = (): boolean => {
    if (!acoesBloqueadasPorPausa) return false;

    if (janelaAtiva) {
      notify.error(
        `Estamos no horário de ${janelaAtiva.nome} (${janelaAtiva.horaInicio} às ${janelaAtiva.horaFim}). ` +
          `As ações de atividade voltam às ${janelaAtiva.horaFim}.`,
      );
      return true;
    }

    if (foraDoExpediente) {
      notify.error(
        `${foraDoExpediente.descricao}. As ações de atividade ficam disponíveis no horário de funcionamento.`,
      );
      return true;
    }

    return false;
  };

  const [isHelpOpen, setIsHelpOpen] = useState(false);

  // A mesma tela atende o COLABORADOR ("Acompanhamento de Veículos") e o Chefe/ADMIN
  // ("Tempo e SLA") — a ajuda muda conforme quem está olhando.
  const helpSectionsColaborador: ComoFuncionaSecao[] = [
    {
      titulo: 'O que aparece aqui',
      conteudo: (
        <>
          <p>
            Em <strong>Meus Veículos</strong> ficam os veículos em que você foi colocado para
            trabalhar. Se um veículo não aparece, é porque o Chefe de Oficina ainda não incluiu
            você nele — procure-o.
          </p>
          <p>
            Um mesmo veículo pode ter <strong>mais de um colaborador</strong> ao mesmo tempo: cada
            um inicia e finaliza a própria atividade, e as horas são contadas separadamente para
            cada pessoa.
          </p>
          <p>
            Quando você <strong>finaliza</strong> uma atividade, o veículo <strong>sai desta
            lista</strong> — suas horas foram para a aprovação do Chefe de Oficina e não há mais
            nada a fazer nele por enquanto. Ele volta a aparecer se o Chefe devolver o veículo
            para você depois de aprovar ou reprovar as horas. A exceção é quando você finaliza
            declarando que o serviço ficou <strong>inacabado</strong>: aí ele continua aqui, para
            ser retomado.
          </p>
        </>
      ),
    },
    {
      titulo: 'Quando o veículo tem duas esteiras ao mesmo tempo',
      conteudo: (
        <>
          <p>
            Um veículo pode ter <strong>serviço em curso em mais de uma esteira ao mesmo tempo</strong>:
            ele está numa etapa — por exemplo, Desmontagem — e o Chefe de Oficina abriu outra
            esteira <strong>em paralelo</strong>, para que outro setor trabalhe nele sem que o
            veículo precise sair da etapa em que está.
          </p>
          <p>
            Quando isso acontece, o card avisa. E ao <strong>iniciar a atividade</strong> o sistema
            pergunta <strong>em qual esteira você vai trabalhar</strong>. Escolha a do serviço que{' '}
            <strong>você</strong> vai fazer: é nela que as suas horas são apontadas, e é a{' '}
            <strong>especialidade dela</strong> que o sistema confere. Um mecânico num veículo que
            está em Desmontagem escolhe <strong>Mecânica</strong>, e trabalha normalmente.
          </p>
          <p>
            Se o veículo tiver só uma esteira em curso — o caso normal —, nada muda: a pergunta não
            aparece e a atividade começa na etapa do veículo.
          </p>
          <p>
            Ao finalizar declarando o serviço como <strong>concluído</strong>, a esteira aberta em
            paralelo se fecha sozinha. Se você finalizar como <strong>inacabado</strong>, ela
            continua aberta e você retoma de onde parou depois.
          </p>
        </>
      ),
    },
    {
      titulo: 'Sub-tarefas: marcar o que já terminou',
      conteudo: (
        <>
          <p>
            Quando a etapa em que o veículo está tem <strong>sub-tarefas</strong> cadastradas, elas
            aparecem no card do veículo. Marque cada uma <strong>assim que terminar</strong> — não
            precisa esperar o fim do serviço.
          </p>
          <p>
            Se o veículo tem esteira aberta <strong>em paralelo</strong>, as sub-tarefas dela também
            ficam disponíveis: marque as do serviço que você está fazendo.
          </p>
          <p>
            Cada marcação pede a <strong>sua senha</strong>, porque o registro fica com o seu nome e
            a data. É o que a Recepção usa para responder ao associado o que já foi feito no carro
            dele, sem precisar ligar para a oficina.
          </p>
          <p>
            Marcar sub-tarefa <strong>não encerra a etapa</strong>: quem declara que o serviço
            acabou continua sendo você, ao finalizar a atividade. E dá para{' '}
            <strong>desmarcar</strong> se marcou por engano — também com senha.
          </p>
          <p>
            Se você finalizar o serviço como concluído deixando sub-tarefas sem marcar, o sistema
            avisa, mas <strong>não impede</strong>. O que ficou sem marcar continua registrado como
            pendente e o Chefe de Oficina consegue ver.
          </p>
        </>
      ),
    },
    {
      titulo: 'Iniciar, pausar e finalizar',
      conteudo: (
        <>
          <p>
            Ao iniciar, pausar, retomar ou finalizar uma atividade, o sistema pede a
            <strong> sua senha</strong>. É a forma de garantir que o apontamento de horas é seu.
          </p>
          <p>
            Você pode ter <strong>uma atividade em andamento por vez</strong>. Atividades{' '}
            <strong>pausadas</strong> não impedem começar outra: você pode pausar o serviço de um
            veículo e iniciar a atividade em outro — a pausada fica esperando você retomar ou
            finalizar. O tempo pausado não conta como trabalhado.
          </p>
          <p>
            Para <strong>retomar</strong> uma atividade pausada, você não pode ter outra em
            andamento: pause ou finalize a atual primeiro.
          </p>
          <p>
            <strong>Pausar é diferente de finalizar como inacabado.</strong> A pausa mantém a
            atividade sua: as horas só vão para a aprovação do Chefe de Oficina quando você
            finalizar, e é você quem retoma. Finalizar como inacabado encerra a atividade (as horas
            vão para aprovação) e deixa o serviço registrado como pendência do veículo, que pode ser
            retomada por qualquer colaborador quando o veículo voltar para a esteira.
          </p>
          <p>
            Atenção: se o veículo com atividade pausada for <strong>movido de esteira</strong> ou
            tiver a <strong>liberação removida</strong>, sua atividade é encerrada automaticamente e
            as horas vão para aprovação — o tempo trabalhado nunca se perde.
          </p>
        </>
      ),
    },
    {
      titulo: 'Quando não é possível iniciar',
      conteudo: (
        <>
          <p>
            O sistema bloqueia o início quando o veículo está aguardando a próxima etapa, quando a
            esteira atual não aceita colaborador, quando existe uma mudança de esteira esperando
            aprovação do Chefe, ou quando o veículo <strong>ainda não foi liberado pela
            Administração</strong>. A mensagem sempre diz o motivo.
          </p>
          <p>
            Você também só trabalha nas esteiras da sua <strong>especialidade</strong>. Se o veículo
            for movido para uma esteira que não é sua, você é retirado dele — as{' '}
            <strong>horas que já trabalhou não são perdidas</strong>: elas vão para a aprovação do
            Chefe de Oficina, e você recebe um aviso explicando o que aconteceu.
          </p>
        </>
      ),
    },
    {
      titulo: 'Horários de pausa (almoço e lanche)',
      conteudo: (
        <>
          <p>
            Nos horários de pausa definidos pela administração — como o almoço —, os botões de{' '}
            <strong>Iniciar</strong>, <strong>Pausar</strong>, <strong>Retomar</strong> e{' '}
            <strong>Finalizar</strong> ficam bloqueados, e um aviso no topo mostra até que horário.
            O bloqueio começa e termina sozinho, sem você precisar recarregar a tela.
          </p>
          <p>
            O horário é <strong>o seu</strong>: como a oficina tem turnos de almoço diferentes, o
            bloqueio segue a janela em que você foi incluído. Se o seu almoço é às 12:30, a tela não
            trava às 11:30 junto com o pessoal do outro turno.
          </p>
          <p>
            Esse tempo é <strong>descontado do tempo trabalhado automaticamente</strong> quando a
            atividade é finalizada — você não precisa pausar para o almoço. As outras telas do
            sistema continuam funcionando normalmente durante o horário.
          </p>
        </>
      ),
    },
    {
      titulo: 'Fora do horário de funcionamento',
      conteudo: (
        <>
          <p>
            Fora do horário de funcionamento da oficina — antes de abrir, depois de fechar e nos dias
            em que ela não abre — as ações de atividade também ficam <strong>bloqueadas</strong>, com
            um aviso no topo dizendo o motivo.
          </p>
          <p>
            Esse tempo é <strong>descontado do tempo trabalhado</strong> do mesmo jeito. Por isso,
            se você esquecer de finalizar uma atividade e ela ficar aberta até o dia seguinte, a
            noite não entra como tempo trabalhado — só as horas dentro do expediente são contadas.
          </p>
        </>
      ),
    },
    {
      titulo: 'Terminei o serviço, e agora?',
      conteudo: (
        <>
          <p>
            Ao finalizar, o sistema pergunta se <strong>o serviço daquela esteira foi concluído</strong>.
            Se ainda não terminou (por exemplo, a peça não chegou), escolha <strong>Não, ficou
            inacabado</strong> e escolha o <strong>Motivo da parada</strong> na lista (quando a oficina
            tiver motivos cadastrados, a escolha é obrigatória; o motivo <strong>Outro</strong> pede que
            você descreva o detalhe). O serviço fica registrado como pendente, o
            veículo pode ir para outra esteira adiantar outros trabalhos e, quando voltar, você
            <strong> retoma de onde parou</strong>. Ninguém é desvinculado do veículo e o Chefe de Oficina
            é avisado. O aviso na tela de finalização explica o que vai acontecer em cada opção.
          </p>
          <p>
            Ao finalizar você também pode <strong>sugerir a próxima esteira</strong> do veículo — quem
            decide é o Chefe de Oficina. Você nunca move o veículo direto.
          </p>
          <p>
            Suas horas ficam <strong>aguardando aprovação</strong>. Se forem reprovadas, aparece
            aqui o aviso <strong>Horas reprovadas</strong> com a justificativa; ele some quando você
            confirma que leu ou quando inicia uma nova atividade no mesmo veículo.
          </p>
          <p>
            Sua atividade também pode ser <strong>encerrada pelo sistema</strong> — por exemplo se o
            veículo for entregue, se o Chefe mudar a esteira ou se a Administração remover a
            liberação. Nesses casos as horas trabalhadas são preservadas e enviadas para aprovação,
            e você recebe um aviso.
          </p>
        </>
      ),
    },
    {
      titulo: 'Meu Histórico de Tempos',
      conteudo: (
        <p>
          A outra aba mostra tudo que você já apontou, com a situação de cada atividade (aguardando
          aprovação, aprovada ou reprovada) e o tempo de cada uma. É a sua consulta; não é possível
          alterar apontamentos por aqui.
        </p>
      ),
    },
  ];

  const helpSectionsChefe: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela mostra',
      conteudo: (
        <p>
          Uma linha por <strong>atendimento</strong> — cada entrada de um veículo na oficina,
          inclusive os que ainda não tiveram atividade e os já entregues. As colunas trazem o tempo
          total trabalhado, a quantidade de atividades e quantas horas estão aguardando sua
          aprovação.
        </p>
      ),
    },
    {
      titulo: 'Sub-tarefas do serviço',
      conteudo: (
        <>
          <p>
            Ao abrir um veículo, o bloco <strong>O que já foi feito</strong> mostra as sub-tarefas
            de cada etapa e quais os colaboradores já concluíram, com nome e horário.
          </p>
          <p>
            As sub-tarefas que valem para cada veículo são escolhidas por você, no{' '}
            <strong>planejamento de serviços</strong>. O catálogo de cada esteira é cadastrado em
            Administração do Sistema.
          </p>
          <p>
            Sub-tarefa não marcada continua <strong>pendente</strong> mesmo depois de o serviço da
            etapa ser concluído — nada é descartado, e é assim que você enxerga o que ficou para
            trás.
          </p>
        </>
      ),
    },
    {
      titulo: 'Como o tempo é contado',
      conteudo: (
        <>
          <p>
            O tempo trabalhado é a <strong>soma das horas de todos os colaboradores</strong>: dois
            colaboradores uma hora cada no mesmo veículo somam duas horas de trabalho. É o custo de
            mão de obra, não o tempo de relógio.
          </p>
          <p>
            O prazo de permanência do veículo (dias no setor e na oficina) é contado à parte, pelo
            tempo em que ele fica em cada esteira — não muda com a quantidade de pessoas.
          </p>
        </>
      ),
    },
    {
      titulo: 'Aprovar e reprovar horas',
      conteudo: (
        <>
          <p>
            Clique na <strong>placa</strong> para abrir o atendimento: dados do veículo, tempo por
            setor, todas as atividades e o histórico de esteiras. A aprovação acontece ali, uma
            atividade por vez, e sempre com uma confirmação explicando o efeito no veículo.
          </p>
          <p>
            Aprovar a última atividade <strong>concluída</strong> leva o veículo para <strong>Aguardando
            próxima etapa</strong> e libera todos os colaboradores. Enquanto <strong>qualquer um</strong>
            ainda estiver trabalhando, o veículo não é movido. Reprovar afeta somente o colaborador
            daquelas horas, que vê a justificativa na tela dele.
          </p>
          <p>
            Uma atividade <strong>pausada</strong> também segura o veículo, e o colaborador pode
            estar trabalhando em outro nesse meio-tempo — só é possível aprovar horas de atividade
            já <strong>finalizada</strong>. Se precisar liberar o veículo, mover a esteira encerra
            as atividades abertas automaticamente: as horas são preservadas e caem aqui para
            aprovação, e o serviço fica registrado como inacabado.
          </p>
          <p>
            Quando o colaborador finaliza declarando que o serviço ficou <strong>inacabado</strong>,
            aprovar as horas <strong>não move o veículo</strong> e não desvincula ninguém: o serviço
            segue como pendência e é retomado quando o veículo voltar para aquela esteira. A coluna
            de pendências indica os dois casos — horas aguardando aprovação e serviço inacabado.
          </p>
          <p>
            No campo <strong>Colaborador do veículo após esta decisão</strong>, o padrão é
            <strong> Não atribuir</strong>: ao aprovar (ou reprovar) as horas, o veículo fica sem
            colaborador nenhum. Escolha <strong>Manter os colaboradores atuais</strong> para deixar
            quem está nele, ou selecione uma pessoa para adicioná-la. A confirmação sempre descreve
            o que vai acontecer antes de executar.
          </p>
          <p>
            Ao aprovar horas com uma <strong>esteira sugerida</strong>, a confirmação avisa se algum
            colaborador do veículo não tem a <strong>especialidade</strong> da esteira de destino:
            essas pessoas saem do veículo e as horas em aberto delas vêm para aprovação. O seletor
            de próximo colaborador oferece <strong>apenas quem pode trabalhar</strong> naquela
            esteira (e nas abertas em paralelo); se ele vier vazio, é porque ninguém tem a
            especialidade — o que se resolve em Administração do Sistema.
          </p>
          <p>
            Quando o veículo tem esteira aberta <strong>em paralelo</strong>, as atividades dela{' '}
            <strong>não</strong> são encerradas ao mover a etapa, e quem está trabalhando nela{' '}
            <strong>continua no veículo</strong> mesmo com a opção "Não atribuir" — o serviço em
            paralelo segue em curso. As horas dessa atividade aparecem aqui na esteira em que foram
            apontadas, que pode não ser a etapa em que o veículo está.
          </p>
        </>
      ),
    },
    {
      titulo: 'Filtros e exportação',
      conteudo: (
        <p>
          É possível filtrar por placa, modelo ou nº do evento, por esteira, por período de entrada,
          por colaborador e mostrar somente atendimentos com horas pendentes. A exportação em CSV
          sai no nível de <strong>apontamentos</strong> (uma linha por atividade), com os filtros
          próprios do export.
        </p>
      ),
    },
  ];

  const helpSections = isCollaboratorView ? helpSectionsColaborador : helpSectionsChefe;

  const [myRejections, setMyRejections] = useState<any[]>([]);
  const [dismissingRejection, setDismissingRejection] = useState<string | null>(null);
  const [rejectionsOpen, setRejectionsOpen] = useState(false);
  const rejectionsPanelRef = useRef<HTMLDivElement | null>(null);

  const [interruptedNotifs, setInterruptedNotifs] = useState<Array<{
    id: string; message: string; carId: string; carPlate: string; createdAt: string;
  }>>([]);
  const [interruptedOpen, setInterruptedOpen] = useState(false);
  const [dismissingInterrupted, setDismissingInterrupted] = useState<string | null>(null);
  const interruptedPanelRef = useRef<HTMLDivElement | null>(null);

  // Tab control state for logged-in Collaborator
  const [activeTab, setActiveTab] = useState<'veiculos' | 'historico'>('veiculos');

  // Collaborator cars visual status history & parts
  // Nenhuma parte da tela lê este estado hoje (só o histórico de ícones é exibido); mantido
  // apenas o setter, que continua sendo alimentado ao carregar o histórico de esteiras do carro.
  const [, setKanbanStatusHistory] = useState<{ [carId: string]: any[] }>({});
  const [statusIconsHistory, setStatusIconsHistory] = useState<{ [carId: string]: any[] }>({});
  const [plannedStatusesByCarId, setPlannedStatusesByCarId] = useState<Record<string, string[]>>({});


  // Modals and Filter Overlay states
  const [isCollaboratorModalOpen, setIsCollaboratorModalOpen] = useState(false);
  const [collaboratorSearchInput, setCollaboratorSearchInput] = useState('');
  const [collaboratorPage, setCollaboratorPage] = useState(1);

  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    esteiraId: '',
    entryStartDate: '',
    entryEndDate: '',
    hasPending: false,
  });

  // Start modal state
  /**
   * at-99 (ADR 0050): `esteirasDisponiveis` são as esteiras ATIVAS do veículo — a etapa
   * (principal) e as abertas em paralelo. Com mais de uma, o colaborador escolhe onde a hora
   * dele vai ser apontada; com uma só, o campo não aparece e nada muda.
   */
  const [startModalState, setStartModalState] = useState<{
    isOpen: boolean;
    carId: string;
    carPlate?: string;
    carModel?: string;
    esteirasDisponiveis: Array<{ esteiraId: string; nome: string; principal: boolean }>;
  } | null>(null);
  /** A esteira escolhida no modal. Vazio = a principal (o comportamento de sempre). */
  const [startEsteiraId, setStartEsteiraId] = useState('');
  const [startPassword, setStartPassword] = useState('');
  const [startPending, setStartPending] = useState(false);

  // Resume modal state
  const [resumeModalState, setResumeModalState] = useState<{ isOpen: boolean; session: WorkSession; carPlate?: string } | null>(null);
  const [resumePassword, setResumePassword] = useState('');
  const [resumePending, setResumePending] = useState(false);

  // Pause modal state
  const [pauseSession, setPauseSession] = useState<WorkSession | null>(null);
  const [pauseReason, setPauseReason] = useState('');
  const [pausePassword, setPausePassword] = useState('');

  // Next sector flow state
  const [nextSectorSession, setNextSectorSession] = useState<WorkSession | null>(null);
  const [nextSectorCarId, setNextSectorCarId] = useState<string | null>(null);
  const [availableActivities, setAvailableActivities] = useState<any[]>([]);
  const [selectedNextSector, setSelectedNextSector] = useState('');
  const [stopPassword, setStopPassword] = useState('');
  // at-46: declaração do desfecho do serviço da esteira ao finalizar (null = ainda não escolheu).
  const [servicoConcluido, setServicoConcluido] = useState<boolean | null>(null);
  const [pausaObservacao, setPausaObservacao] = useState('');
  // at-134 (ADR 0065): o motivo da parada, quando o serviço fica inacabado.
  const [motivoParadaId, setMotivoParadaId] = useState('');
  // Carrega a lista só quando o modal de finalizar abre (é o único ponto que a usa nesta tela).
  const { motivos: motivosDaParada, estado: estadoDosMotivos } = useMotivosDaParada(Boolean(nextSectorSession));
  const pendenciaDaParada =
    servicoConcluido === false
      ? pendenciaDoMotivo(estadoDosMotivos, motivosDaParada, motivoParadaId, pausaObservacao)
      : null;
  const [loadingActivities, setLoadingActivities] = useState(false);

  /**
   * at-89: quantas sub-tarefas da esteira atual ficaram pendentes, por veículo. Alimenta o aviso
   * do modal de finalização — o colaborador PODE finalizar com pendências (nada é descartado, elas
   * seguem registradas), mas precisa saber disso antes de declarar o serviço concluído.
   */
  const [pendenciasSubtarefas, setPendenciasSubtarefas] = useState<Record<string, number>>({});

  const registrarProgressoSubtarefas = useCallback(
    (carId: string, progresso: { esteiras: Array<{ atual: boolean; total: number; totalConcluidas: number }> }) => {
      const atual = progresso.esteiras.find((e) => e.atual);
      const pendentes = atual ? atual.total - atual.totalConcluidas : 0;
      setPendenciasSubtarefas((prev) => (prev[carId] === pendentes ? prev : { ...prev, [carId]: pendentes }));
    },
    [],
  );

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportApprovers, setExportApprovers] = useState<Array<{ id: string; fullName?: string; username?: string }>>([]);
  const [exportFilters, setExportFilters] = useState<WorkSessionExportFilters>({
    startDate: '',
    endDate: '',
    collaboratorId: '',
    sector: '',
    status: '',
    approvalStatus: '',
    minDuration: '',
    maxDuration: '',
    q: '',
    groupBy: '',
    includeSummary: 'false',
  });

  // Sessões do próprio colaborador — usadas apenas pelos cards "Meus Veículos"
  // (estado da sessão ativa por carro).
  const queryFilters = useMemo(
    () => ({
      collaboratorId: userId,
      page: 1,
      limit: 50,
    }),
    [userId],
  );

  const workSessionsQuery = useWorkSessionsQuery(queryFilters, Boolean(userId) && isCollaboratorView);

  const startMutation = useStartWorkSessionMutation();
  const stopMutation = useStopWorkSessionMutation();
  const pauseMutation = usePauseWorkSessionMutation();
  const resumeMutation = useResumeWorkSessionMutation();

  const sessions: WorkSession[] = useMemo(
    () => workSessionsQuery.data?.sessions || [],
    [workSessionsQuery.data],
  );

  // Busca da listagem de atendimentos (tabela principal).
  const [atendimentosLoadedOnce, setAtendimentosLoadedOnce] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const fetchAtendimentos = async () => {
      setLoadingAtendimentos(true);
      try {
        const response = await getAtendimentos({
          q: searchQuery || undefined,
          esteiraId: esteiraFilter || undefined,
          entryStartDate: entryStartFilter || undefined,
          entryEndDate: entryEndFilter || undefined,
          hasPending: hasPendingFilter ? 'true' : undefined,
          collaboratorId: collaboratorIdFilter || undefined,
          page,
          limit,
        });
        if (!cancelled) {
          setAtendimentos(response.atendimentos || []);
          setAtendimentosTotal(response.total || 0);
        }
      } catch (error) {
        if (!cancelled) {
          setAtendimentos([]);
          setAtendimentosTotal(0);
          // Grade vazia por falha é indistinguível de "não há atendimentos" — foi esse silêncio
          // que escondeu o 403 do at-36 em produção.
          notify.errorFrom(error, 'Não foi possível carregar a lista de atendimentos. Recarregue a página; se persistir, acione a TI.');
        }
      } finally {
        if (!cancelled) {
          setLoadingAtendimentos(false);
          setAtendimentosLoadedOnce(true);
        }
      }
    };

    fetchAtendimentos();
    return () => {
      cancelled = true;
    };
  }, [userId, searchQuery, esteiraFilter, entryStartFilter, entryEndFilter, hasPendingFilter, collaboratorIdFilter, page, limit, atendimentosRefresh]);

  /**
   * at-68 (BUG-V54): o loader de tela cheia só vale quando a TABELA é o conteúdo.
   *
   * `loading` gateava a página inteira esperando a listagem de atendimentos — que, para o
   * COLABORADOR, é a aba "Meu Histórico". Ele abria a tela (é a inicial dele desde o at-31)
   * e ficava olhando um spinner cinza por causa de uma consulta que alimenta outra aba,
   * enquanto os cards dos veículos dele já estavam prontos.
   */
  const carregandoTabela = !atendimentosLoadedOnce && loadingAtendimentos;
  const loading = carregandoTabela && !isCollaboratorView;
  const total = atendimentosTotal;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const startIndex = total === 0 ? 0 : (page - 1) * limit + 1;
  const endIndex = Math.min(page * limit, total);

  const activeSessionByCar = useMemo(() => {
    const map: Record<string, WorkSession> = {};
    sessions.forEach((session) => {
      if ((session.status === 'IN_PROGRESS' || session.status === 'PAUSED') && session.carId && !map[session.carId]) {
        map[session.carId] = session;
      }
    });
    return map;
  }, [sessions]);

  /**
   * Veículos com trabalho pendente PARA MIM.
   *
   * O backend lista por vínculo aberto (`findCarsByCollaboratorId`), e o desvínculo só acontece
   * quando o Chefe resolve as horas (ADR 0020) — então, entre finalizar e a aprovação, o veículo
   * continua "meu" para o backend. Antes isso era escondido por estado local do React, que
   * morria em qualquer F5 e fazia o cartão reaparecer já finalizado.
   *
   * Agora o estado é DERIVADO do servidor (sobrevive a refresh): sai da lista quem já teve a
   * atividade finalizada e está esperando o Chefe. Volta sozinho quando ele aprova/reprova —
   * a sessão deixa de ser PENDING — ou quando o vínculo é fechado (aí o backend nem devolve).
   */
  const operationalCars = useMemo(() => {
    if (!isCollaboratorView) return [];

    // Sessão mais recente minha em cada carro.
    const ultimaSessaoPorCarro: Record<string, WorkSession> = {};
    sessions.forEach((session) => {
      if (!session.carId) return;
      const atual = ultimaSessaoPorCarro[session.carId];
      if (!atual || new Date(session.startedAt).getTime() > new Date(atual.startedAt).getTime()) {
        ultimaSessaoPorCarro[session.carId] = session;
      }
    });

    return myCars.filter((car) => {
      // Trabalhando ou pausado agora: sempre visível.
      if (activeSessionByCar[car.id]) return true;

      // Serviço inacabado na esteira ATUAL continua visível — é o "Retomar Serviço" (at-46).
      const temServicoRetomavel = (car.servicosInacabados ?? []).some(
        (s: ServicoInacabado) => s.estado === 'PAUSADA' && s.esteiraId === car.esteiraId,
      );
      if (temServicoRetomavel) return true;

      const ultima = ultimaSessaoPorCarro[car.id];
      // Só esconde se a sessão pendente é de uma esteira ATIVA do veículo (principal ou
      // paralela). Sessão de esteira anterior (encerrada à força quando o veículo mudou de
      // esteira) não diz nada sobre o trabalho atual: escondia o carro de quem acabara de
      // ser vinculado de novo (KYS6261, 28/10/2026).
      const esteirasAtivas = new Set<string>([
        car.esteiraId,
        ...(car.esteirasParalelas ?? []).map((p: { esteiraId: string }) => p.esteiraId),
      ]);
      const sessaoDaEsteiraAtiva = !ultima?.esteiraId || esteirasAtivas.has(ultima.esteiraId);
      const aguardandoChefe =
        sessaoDaEsteiraAtiva &&
        ultima?.status === 'FINISHED' &&
        ultima?.approvalStatus === 'PENDING' &&
        // Finalização inacabada não esconde: a pendência é do veículo e pode voltar para ele.
        ultima?.servicoConcluido !== false;

      return !aguardandoChefe;
    });
  }, [myCars, sessions, activeSessionByCar, isCollaboratorView]);

  // Collaborator search/pagination for modal (now backend-driven)
  const COLLABORATORS_PER_PAGE = 5;
  const totalCollaboratorPages = Math.max(1, Math.ceil(totalCollaboratorsCount / COLLABORATORS_PER_PAGE));
  const filteredCollaborators = collaborators;
  const paginatedCollaborators = collaborators;

  // Dynamic Column calculation refs and states
  const headers = [
    'Placa',
    'Modelo',
    'Entrada',
    'Tempo trabalhado',
    'Atividades',
    'Pendências',
    'Status atual',
  ];

  const defaultColumnWidths = [
    120, 150, 115, 140, 110, 130, 150
  ];

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const esteiraAtualLabel = useCallback(
    (atendimento: AtendimentoResumo) =>
      atendimento.esteiraChave === ESTEIRA.ENTREGA
        ? 'Entregue'
        : (atendimento.esteiraNome || '-'),
    [],
  );

  // Valores renderizados por linha, memoizados para o hook de medição (at-43).
  const getColumnValues = useCallback(
    (atendimento: AtendimentoResumo) => [
      atendimento.plate || '-',
      atendimento.model || '-',
      atendimento.entryDate ? new Date(atendimento.entryDate).toLocaleDateString('pt-BR') : '-',
      formatDuration(atendimento.totalWorkedSeconds),
      String(atendimento.activitiesCount),
      atendimento.pendingCount > 0 ? `${atendimento.pendingCount} pendente(s)` : '-',
      esteiraAtualLabel(atendimento),
    ],
    [esteiraAtualLabel],
  );

  const tableRows = useMemo(
    () => atendimentos.map(getColumnValues),
    [atendimentos, getColumnValues],
  );

  const { tableColumns, tableWidth } = useTableColumns({
    containerRef: tableScrollRef,
    headerCellsRef: headerRefs,
    headers,
    defaultWidths: defaultColumnWidths,
    rows: tableRows,
    fallbackHeaderFont: '500 13.5px AmpleSoft-Regular, Roboto, sans-serif',
    fallbackCellFont: '400 12px Roboto, sans-serif',
    // A troca de aba remonta o container da tabela — religa o ResizeObserver nele.
    remeasureKey: activeTab,
  });

  const renderPagination = (
    currentPage: number,
    totalPages: number,
    onPageChange: (page: number) => void
  ) => {
    if (totalPages <= 1) return null;

    return (
      <div className="PaginaO">
        <div
          className={`Frame1000003950 ${currentPage === 1 ? 'disabled' : ''}`}
          onClick={() => {
            if (currentPage > 1) {
              onPageChange(currentPage - 1);
            }
          }}
          style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
        >
          <img
            className="imgIcon"
            src={currentPage === 1 ? ArrowLeftGray : ArrowLeftOrange}
            alt="Seta para Esquerda"
            width="24"
            height="24"
          />
          <div
            className="Anterior"
            style={{ color: currentPage === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
          >
            Anterior
          </div>
        </div>
        <div className="Group21">
          <div className="Group20">
            {(() => {
              if (totalPages === 1) {
                return (
                  <div key={1} className="page-number active">
                    1
                    <div className="Ellipse14" />
                  </div>
                );
              }

              const pages = [];
              const maxVisiblePages = 10;

              pages.push(
                <div
                  key={1}
                  className={`page-number ${currentPage === 1 ? 'active' : ''}`}
                  onClick={() => onPageChange(1)}
                >
                  1
                  {currentPage === 1 && <div className="Ellipse14" />}
                </div>
              );

              if (currentPage > maxVisiblePages - 4 && totalPages > maxVisiblePages) {
                pages.push(<span key="left-ellipsis" className="ellipsis">...</span>);
              }

              const startPage = Math.max(
                2,
                Math.min(
                  currentPage - Math.floor((maxVisiblePages - 2) / 2),
                  totalPages - (maxVisiblePages - 2)
                )
              );
              const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <div
                    key={i}
                    className={`page-number ${currentPage === i ? 'active' : ''}`}
                    onClick={() => {
                      onPageChange(i);
                    }}
                  >
                    {i}
                    {currentPage === i && <div className="Ellipse14" />}
                  </div>
                );
              }

              if (endPage < totalPages - 1 && totalPages > maxVisiblePages) {
                pages.push(<span key="right-ellipsis" className="ellipsis">...</span>);
              }

              pages.push(
                <div
                  key={totalPages}
                  className={`page-number ${currentPage === totalPages ? 'active' : ''}`}
                  onClick={() => onPageChange(totalPages)}
                >
                  {totalPages}
                  {currentPage === totalPages && <div className="Ellipse14" />}
                </div>
              );

              return pages;
            })()}
          </div>
        </div>
        <div
          className={`Frame1000003949 ${currentPage === totalPages ? 'disabled' : ''}`}
          onClick={() => {
            if (currentPage < totalPages) {
              onPageChange(currentPage + 1);
            }
          }}
          style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
        >
          <div
            className="PrXima"
            style={{ color: currentPage === totalPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
          >
            Próxima
          </div>
          <img
            className="imgIcon"
            src={currentPage === totalPages ? ArrowRightGray : ArrowRightOrange}
            alt="Seta para Direita"
            width="24"
            height="24"
          />
        </div>
      </div>
    );
  };

  const handleOpenFiltersModal = () => {
    setTempFilters({
      esteiraId: esteiraFilter,
      entryStartDate: entryStartFilter,
      entryEndDate: entryEndFilter,
      hasPending: hasPendingFilter,
    });
    setIsFiltersModalOpen(true);
  };

  const handleApplyFilters = () => {
    setEsteiraFilter(tempFilters.esteiraId);
    setEntryStartFilter(tempFilters.entryStartDate);
    setEntryEndFilter(tempFilters.entryEndDate);
    setHasPendingFilter(tempFilters.hasPending);
    setPage(1);
    setIsFiltersModalOpen(false);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSearchInput('');
    setCollaboratorIdFilter('');
    setEsteiraFilter('');
    setEntryStartFilter('');
    setEntryEndFilter('');
    setHasPendingFilter(false);
    setTempFilters({
      esteiraId: '',
      entryStartDate: '',
      entryEndDate: '',
      hasPending: false,
    });
    setPage(1);
  };

  const refreshAtendimentos = () => setAtendimentosRefresh((n) => n + 1);

  const handleInitiateStop = async (session: WorkSession, carId?: string) => {
    if (bloqueadoPorJanela()) return;
    const resolvedCarId = carId || session.carId;
    setNextSectorSession(session);
    setNextSectorCarId(resolvedCarId);
    setSelectedNextSector('');
    setStopPassword('');
    // at-46: escolha sempre explícita; o motivo já vem sugerido com o da última pausa da sessão.
    setServicoConcluido(null);
    setPausaObservacao(session.pauses?.[session.pauses.length - 1]?.reason ?? '');
    setMotivoParadaId('');
    setLoadingActivities(true);
    try {
      const [activities] = await Promise.all([
        getAtividadesCarro({ carId: resolvedCarId, isActive: true }),
        (async () => {
          try {
            const historyData = await getCarStatusHistory(resolvedCarId);
            const approvedStatusSet = new Set(
              historyData.history
                .filter((status: any) => status.approvalStatus === 'APPROVED')
                .map((status: any) => status.esteiraId || status.status)
            );
            const activeLanes = esteirasAtivas.filter((est) => est.chave !== ESTEIRA.ENTREGA);
            const carStatusIcons = activeLanes.map((est) => ({
              name: est.id,
              chave: est.chave,
              label: est.nome,
              approved: approvedStatusSet.has(est.id) || approvedStatusSet.has(est.chave),
            }));
            setStatusIconsHistory((prev) => ({
              ...prev,
              [resolvedCarId]: carStatusIcons,
            }));
          } catch (historyErr) {
            console.error('Erro ao carregar histórico para stop:', historyErr);
            notify.errorFrom(historyErr, 'Não foi possível carregar o histórico de esteiras do veículo. Recarregue a página; se persistir, acione a TI.');
          }
        })()
      ]);

      const planned = (activities || []).map((x: any) => x.esteiraId || x.status);
      setPlannedStatusesByCarId((prev) => ({
        ...prev,
        [resolvedCarId]: planned,
      }));

      // Mapa de ordem planejada por esteira (id/chave).
      const orderByEsteira = new Map<string, number>();
      (activities || []).forEach((a: any) => {
        const key = a.esteiraId || a.status;
        if (key != null && a.order != null) orderByEsteira.set(key, a.order);
      });

      // Fonte única: todas as esteiras ativas, com shape consistente.
      const combined = esteirasAtivas
        .map((est, index) => {
          const plannedOrder = orderByEsteira.has(est.id)
            ? orderByEsteira.get(est.id)!
            : orderByEsteira.has(est.chave)
              ? orderByEsteira.get(est.chave)!
              : null;
          return {
            status: est.id,
            chave: est.chave,
            nome: est.nome,
            statusLabel: est.nome,
            sistema: est.sistema,
            esteira: est,
            order: plannedOrder,
            _index: index,
          };
        })
        .sort((a, b) => {
          const ordA = a.order ?? 999;
          const ordB = b.order ?? 999;
          if (ordA !== ordB) return ordA - ordB;
          return a._index - b._index;
        });
      setAvailableActivities(combined);
    } catch (error) {
      setAvailableActivities([]);
      // Lista vazia trava a finalizacao da atividade sem dizer o motivo ao colaborador.
      notify.errorFrom(error, 'Não foi possível carregar as esteiras disponíveis. Recarregue a página; se persistir, acione a TI.');
    } finally {
      setLoadingActivities(false);
    }
  };

  const executeConfirmStop = async () => {
    if (!nextSectorSession || !stopPassword || servicoConcluido === null) return;
    // at-134: o motivo é conferido ANTES de tudo — a mudança de esteira (passo 2) é pedida antes
    // do stop, e uma recusa do backend depois dela deixaria o pedido feito e a atividade aberta.
    if (pendenciaDaParada) return;
    // Borda real: modal aberto antes da janela, confirmação já dentro dela.
    if (bloqueadoPorJanela()) return;
    try {
      // 1) Pré-validar a senha com verifyPassword ANTES de alterar o status/esteira do carro
      await verifyPassword(stopPassword);

      // 2) Se há mudança de esteira, solicita APÓS a pré-validação da senha
      if (selectedNextSector && nextSectorCarId) {
        await updateCarStatus(nextSectorCarId, selectedNextSector);
      }
      // 3) Só então finaliza a sessão com a senha e a declaração do desfecho do serviço (at-46)
      const stopResult: any = await stopMutation.mutateAsync({
        id: nextSectorSession.id,
        password: stopPassword,
        servicoConcluido,
        pausaObservacao: servicoConcluido ? undefined : pausaObservacao.trim() || undefined,
        motivoParadaId: servicoConcluido ? undefined : motivoParadaId || undefined,
      });

      // O toast reflete o que DE FATO aconteceu (resposta do backend), não a intenção:
      // com outro colaborador ainda trabalhando na esteira, a declaração fica registrada
      // mas o serviço segue em andamento até o último finalizar (last-one-wins, ADR 0020).
      const esteiraNome = labelDe(nextSectorSession.sector) || nextSectorSession.sector;
      if (servicoConcluido) {
        // at-65 (BUG-V51): `actionSuccess` conjuga o verbo a partir do `action`, então
        // passar uma frase já conjugada como `subject` produzia "Atividade finalizada
        // concluído com sucesso". Frase pronta usa `notify.success` direto.
        notify.success('Atividade finalizada com sucesso.');
      } else if (stopResult?.servicoEstado === 'PAUSADA') {
        notify.success(
          `Atividade finalizada. O serviço de ${esteiraNome} ficou registrado como inacabado e pode ser retomado quando o veículo voltar para esta esteira.`,
        );
      } else {
        notify.info(
          `Atividade finalizada. Sua declaração de serviço inacabado foi registrada, mas outro colaborador ainda está trabalhando em ${esteiraNome} — o serviço ficará como inacabado quando o último finalizar.`,
          { autoClose: 9000 },
        );
      }
      // A saída do veículo da lista é DERIVADA das sessões (ver `operationalCars`), não marcada
      // em estado local — por isso aqui só o refetch: ele traz a sessão já FINISHED/PENDING.
      workSessionsQuery.refetch();
      refreshAtendimentos();
      setMyCarsRefreshTrigger((prev) => prev + 1);
      setNextSectorSession(null);
      setNextSectorCarId(null);
      setAvailableActivities([]);
      setSelectedNextSector('');
      setStopPassword('');
      setServicoConcluido(null);
      setPausaObservacao('');
    } catch (error: any) {
      // Mantém o modal aberto para o usuário escolher outra esteira / corrigir a senha.
      const message = error?.response?.data?.message;
      if (message) {
        notify.error(Array.isArray(message) ? message.join(' ') : message);
      } else {
        notify.actionError(error, { action: 'update', subject: 'sessao' });
      }
    }
  };

  const handleConfirmStop = async () => {
    if (!nextSectorSession) return;
    const isEntregaLane = chaveDe(selectedNextSector) === ESTEIRA.ENTREGA;
    if (isEntregaLane && userType === 'COLABORADOR') {
      notify.error('Colaboradores não podem mover veículos para a esteira de Entrega.');
      return;
    }

    if (isEntregaLane && nextSectorCarId) {
      // at-46: a checagem passou a usar o ESTADO do serviço (helper único) — antes derivava
      // "realizada" de qualquer período APPROVED, o que dava esteira concluída para serviço
      // apenas pausado e desligava o aviso justamente quando ele importa.
      // at-60: quando a consulta falha o helper devolve `verificacaoIndisponivel` (nunca
      // rejeita), e a confirmação abaixo avisa que não deu para verificar em vez de seguir
      // calada como se não houvesse nada em aberto.
      const pendentes = await buscarServicosPendentes(nextSectorCarId, {
        ignorarEsteiraIds: [selectedNextSector],
      });
      const mensagem = mensagemConfirmacaoEntrega(pendentes);

      if (mensagem) {
        setConfirmUnrealizedModal({
          isOpen: true,
          message: mensagem,
          onConfirm: () => {
            setConfirmUnrealizedModal((prev) => ({ ...prev, isOpen: false }));
            executeConfirmStop();
          },
        });
        return;
      }
    }

    executeConfirmStop();
  };

  const handleOpenExportModal = () => {
    setExportFilters({
      startDate: '',
      endDate: '',
      collaboratorId: collaboratorIdFilter,
      sector: '',
      status: '',
      approvalStatus: '',
      minDuration: '',
      maxDuration: '',
      q: searchQuery,
      groupBy: '',
      includeSummary: 'false',
    });
    setIsExportModalOpen(true);
    fetchUsersByTypes(['CHEFE_DE_OFICINA', 'ADMIN'], 1, 100)
      .then((res) => setExportApprovers(res.users || []))
      .catch((error) => {
        setExportApprovers([]);
        notify.errorFrom(error, 'Não foi possível carregar a lista de aprovadores. Recarregue a página; se persistir, acione a TI.');
      });
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      await exportWorkSessionsCsv(exportFilters);
      notify.success('Relatório gerado com sucesso.');
      setIsExportModalOpen(false);
    } catch (error) {
      notify.actionError(error, { action: 'send', subject: 'relatório' });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    // Cookie primeiro, como o JwtAuthGuard do backend (extractToken lê o cookie antes do
    // header) — trocar a ordem aqui descolaria a identidade da tela da identidade efetiva.
    const token = lerTokenDoCookie() || localStorage.getItem('authToken');
    if (!token) return;

    try {
      const decoded = jwtDecode<DecodedToken>(token);
      setUserId(decoded.sub);
      setUserType(decoded.userType);
      if (decoded.username) {
        setUserDisplayName((prev) => prev || decoded.username || '');
      }
    } catch {
      setUserId('');
      setUserType(null);
    }
  }, []);

  useEffect(() => {
    if (!isCollaboratorView || !userId) return;

    getUserById(userId)
      .then((user) => {
        if (user?.fullName || user?.username) {
          setUserDisplayName(user.fullName || user.username);
        }
      })
      .catch(() => {
        // Silencio deliberado (T5, at-60): a falha aqui e puramente cosmetica — a saudacao
        // continua com o username do token e nenhuma acao do colaborador depende disso.
      });
  }, [isCollaboratorView, userId]);

  useEffect(() => {
    if (!isCollaboratorView || !userId) return;

    const fetchRejections = () =>
      getMyRejections()
        .then(setMyRejections)
        // Recorrente (30s): toastId fixo para o aviso nao empilhar enquanto a rede estiver fora.
        .catch(() => notify.warning('Não foi possível atualizar os avisos de horas reprovadas.', { toastId: 'meus-veiculos-rejeicoes' }));

    fetchRejections();

    /**
     * at-68 (BUG-V53): a reprovação chega por evento, e o poll fica como rede de segurança.
     *
     * O backend já emite `work-session:resolved` ao aprovar e ao reprovar (`reject`, via
     * `emitWorkSessionResolved`), e o `useCarStatusSocket` já republica isso como evento de
     * window — só este painel não assinava, então o colaborador esperava **até 30 segundos**
     * para saber que suas horas foram reprovadas. O intervalo continua por segurança (socket
     * caído, aba que dormiu), agora mais largo: com o evento, ele deixou de ser o caminho.
     */
    window.addEventListener('work-session:resolved', fetchRejections);
    const interval = setInterval(fetchRejections, 120_000);
    return () => {
      window.removeEventListener('work-session:resolved', fetchRejections);
      clearInterval(interval);
    };
  }, [isCollaboratorView, userId]);

  useEffect(() => {
    if (!isCollaboratorView) return;

    const handleSessionInterrupted = (e: Event) => {
      const data = (e as CustomEvent).detail as {
        id: string; message: string; carId: string; carPlate: string; createdAt: string;
      };
      setInterruptedNotifs((prev) => {
        if (prev.some((n) => n.id === data.id)) return prev;
        return [data, ...prev];
      });
      setMyCars((prev) => prev.filter((car) => car.id !== data.carId));
    };

    window.addEventListener('notification:session-interrupted', handleSessionInterrupted);
    return () => window.removeEventListener('notification:session-interrupted', handleSessionInterrupted);
  }, [isCollaboratorView]);

  useEffect(() => {
    if (!isCollaboratorView) return;

    const handleCarAssigned = () => {
      setMyCarsRefreshTrigger((n) => n + 1);
    };

    const handleCarUnassigned = (e: Event) => {
      const { carId } = (e as CustomEvent).detail as { carId: string };
      setMyCars((prev) => prev.filter((car) => car.id !== carId));
    };

    // A aprovação do Chefe vincula o colaborador ANTES de efetivar a esteira:
    // o car:assigned chega com o carro ainda na esteira antiga. O
    // car:status-updated é quem sinaliza o estado final — recarrega a lista.
    const handleCarStatusUpdated = () => {
      setMyCarsRefreshTrigger((n) => n + 1);
    };

    window.addEventListener('car:assigned', handleCarAssigned);
    window.addEventListener('car:unassigned', handleCarUnassigned);
    window.addEventListener('car:status-updated', handleCarStatusUpdated);
    return () => {
      window.removeEventListener('car:assigned', handleCarAssigned);
      window.removeEventListener('car:unassigned', handleCarUnassigned);
      window.removeEventListener('car:status-updated', handleCarStatusUpdated);
    };
  }, [isCollaboratorView]);

  const handleClickOutsideRejections = (event: MouseEvent) => {
    if (rejectionsPanelRef.current && !rejectionsPanelRef.current.contains(event.target as Node)) {
      setRejectionsOpen(false);
    }
  };

  useEffect(() => {
    if (rejectionsOpen) {
      document.addEventListener('mousedown', handleClickOutsideRejections);
    } else {
      document.removeEventListener('mousedown', handleClickOutsideRejections);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideRejections);
    };
  }, [rejectionsOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (interruptedPanelRef.current && !interruptedPanelRef.current.contains(e.target as Node)) {
        setInterruptedOpen(false);
      }
    };
    if (interruptedOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [interruptedOpen]);

  useEffect(() => {
    if (isCollaboratorModalOpen) {
      setCollaboratorPage(1);
      setCollaboratorSearchInput('');
    }
  }, [isCollaboratorModalOpen]);

  useEffect(() => {
    if (!isCollaboratorModalOpen || !isChefeView) return;

    const fetchUsers = async () => {
      setLoadingCollaborators(true);
      try {
        let response;
        if (collaboratorSearchInput.trim()) {
          response = await searchCollaborators(
            collaboratorSearchInput.trim(),
            collaboratorPage,
            COLLABORATORS_PER_PAGE
          );
        } else {
          response = await fetchCollaborators(
            collaboratorPage,
            COLLABORATORS_PER_PAGE
          );
        }
        setCollaborators(response.users || []);
        setTotalCollaboratorsCount(response.total || 0);
      } catch (error) {
        console.error('Erro ao carregar colaboradores no modal:', error);
        setCollaborators([]);
        setTotalCollaboratorsCount(0);
        notify.errorFrom(error, 'Não foi possível carregar a lista de colaboradores. Recarregue a página; se persistir, acione a TI.');
      } finally {
        setLoadingCollaborators(false);
      }
    };

    const delayDebounce = setTimeout(
      () => {
        fetchUsers();
      },
      collaboratorSearchInput.trim() ? 300 : 0
    );

    return () => clearTimeout(delayDebounce);
  }, [isCollaboratorModalOpen, collaboratorPage, collaboratorSearchInput, isChefeView]);

  useEffect(() => {
    const loadCarStatusHistory = async (carId: string) => {
      try {
        const historyData = await getCarStatusHistory(carId);

        const approvedStatuses = (historyData.history as any[])
          .filter((status: any) => status.approvalStatus === 'APPROVED')
          .sort((a: any, b: any) => new Date(b.changeDate).getTime() - new Date(a.changeDate).getTime());

        const latestApprovedStatus = approvedStatuses.length > 0 ? (approvedStatuses[0].esteiraId || approvedStatuses[0].status) : null;

        const activeLanes = esteirasAtivas.filter((est) => est.chave !== ESTEIRA.ENTREGA);

        const carStatusesForKanban = activeLanes.map((est) => ({
          name: est.id,
          approved: est.id === latestApprovedStatus || est.chave === latestApprovedStatus,
        }));

        setKanbanStatusHistory((prevHistory) => ({
          ...prevHistory,
          [carId]: carStatusesForKanban,
        }));

        const approvedStatusSet = new Set(
          (historyData.history as any[])
            .filter((status: any) => status.approvalStatus === 'APPROVED')
            .map((status: any) => status.esteiraId || status.status)
        );

        const carStatusIcons = activeLanes.map((est) => ({
          name: est.id,
          chave: est.chave,
          label: est.nome,
          approved: approvedStatusSet.has(est.id) || approvedStatusSet.has(est.chave),
        }));

        setStatusIconsHistory((prevHistory) => ({
          ...prevHistory,
          [carId]: carStatusIcons,
        }));
      } catch (error) {
        console.error(`Erro ao carregar histórico de status para o carro ${carId}:`, error);
        // Roda uma vez por veiculo do colaborador: toastId fixo para nao virar N toasts iguais.
        notify.error('Não foi possível carregar o histórico de esteiras dos seus veículos. Recarregue a página; se persistir, acione a TI.', {
          toastId: 'meus-veiculos-historico',
        });
      }
    };

    const loadCarActivities = async (carId: string) => {
      try {
        const activities = await getAtividadesCarro({ carId, isActive: true });
        const planned = (activities || []).map((x: any) => x.esteiraId || x.status);
        setPlannedStatusesByCarId((prev) => ({
          ...prev,
          [carId]: planned,
        }));
      } catch (error) {
        console.error(`Erro ao carregar atividades do carro ${carId}:`, error);
        // Roda uma vez por veiculo do colaborador: toastId fixo para nao virar N toasts iguais.
        notify.error('Não foi possível carregar as etapas planejadas dos seus veículos. Recarregue a página; se persistir, acione a TI.', {
          toastId: 'meus-veiculos-atividades',
        });
      }
    };

    const loadMyCars = async () => {
      if (!isCollaboratorView || !userId) return;

      try {
        const cars = await getCarsByCollaborator(userId);
        const filteredCars = (cars || []).filter((car: any) => chaveDe(car.status) !== ESTEIRA.ENTREGA);
        setMyCars(filteredCars);
        filteredCars.forEach((car: any) => {
          loadCarStatusHistory(car.id);
          loadCarActivities(car.id);
        });
      } catch {
        // Desde a at-44 a rota devolve [] quando não há carros — cair aqui é erro real
        // (rede, permissão). O catch silencioso escondeu o 403 do at-36 em produção.
        setMyCars([]);
        notify.error('Erro ao carregar seus veículos. Recarregue a página; se persistir, acione a TI.');
      }
    };

    loadMyCars();
    // 'activeLanesIds' é o proxy estável de 'esteirasAtivas' (string de ids) usado justamente
    // para o efeito reagir à mudança de CONTEÚDO das esteiras sem depender da identidade do
    // array (um refetch do react-query com o mesmo conteúdo troca a identidade e refaria a
    // busca dos veículos do colaborador à toa). 'chaveDe' passou a ser estável (at-43) e
    // entrou nas dependências reais.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollaboratorView, userId, myCarsRefreshTrigger, activeLanesIds, chaveDe]);

  const handleOpenStartModal = (car: any) => {
    if (bloqueadoPorJanela()) return;

    /**
     * at-99: a etapa do veículo mais as esteiras abertas em paralelo.
     *
     * A lista vem do próprio veículo (`esteirasParalelas`, que já chega nas listagens em lote) —
     * sem requisição a mais no clique. Com uma esteira só, o seletor não é renderizado e o
     * `esteiraId` não é enviado, o que mantém o caminho de sempre e a compatibilidade com o
     * backend anterior à entrega.
     */
    const paralelas = (car.esteirasParalelas ?? []) as Array<{ esteiraId: string; nome: string }>;
    const esteirasDisponiveis = [
      {
        esteiraId: car.esteiraId,
        nome: labelDe(car.status) || car.esteiraId,
        principal: true,
      },
      ...paralelas.map((p) => ({ esteiraId: p.esteiraId, nome: p.nome, principal: false })),
    ];

    setStartModalState({
      isOpen: true,
      carId: car.id,
      carPlate: car.plate,
      carModel: car.model,
      esteirasDisponiveis,
    });
    setStartEsteiraId('');
    setStartPassword('');
  };

  const handleConfirmStart = async () => {
    if (!startModalState || !startPassword) return;
    if (bloqueadoPorJanela()) return;
    setStartPending(true);
    try {
      await startMutation.mutateAsync({
        carId: startModalState.carId,
        password: startPassword,
        // at-99: só envia quando houve escolha. Sem isso, o backend anterior à entrega
        // devolveria 400 por campo desconhecido (`forbidNonWhitelisted`).
        ...(startEsteiraId ? { esteiraId: startEsteiraId } : {}),
      });
      const esteiraEscolhida = startModalState.esteirasDisponiveis.find(
        (e) => e.esteiraId === startEsteiraId,
      );
      notify.success(
        esteiraEscolhida && !esteiraEscolhida.principal
          ? `Atividade iniciada em ${esteiraEscolhida.nome}, aberta em paralelo neste veículo. Suas horas serão apontadas nessa esteira.`
          : 'Atividade iniciada com sucesso.',
      );
      workSessionsQuery.refetch();
      refreshAtendimentos();

      const matching = myRejections.filter((r) => r.carId === startModalState.carId);
      if (matching.length > 0) {
        try {
          await Promise.all(matching.map((r) => acknowledgeRejection(r.id)));
          setMyRejections((prev) => prev.filter((r) => r.carId !== startModalState.carId));
        } catch (err) {
          console.error('Erro ao descartar rejeicoes:', err);
          // A atividade iniciou; o que falhou foi so o descarte do aviso, que segue na tela.
          notify.actionError(err, { action: 'delete', subject: 'os avisos de horas reprovadas' });
        }
      }
      setStartModalState(null);
      setStartPassword('');
      setStartEsteiraId('');
    } catch (error: any) {
      const message = error?.response?.data?.message;
      if (message) {
        notify.error(Array.isArray(message) ? message.join(' ') : message);
      } else {
        notify.actionError(error, { action: 'update', subject: 'sessao' });
      }
    } finally {
      setStartPending(false);
    }
  };

  const handleOpenPauseModal = (session: WorkSession) => {
    if (bloqueadoPorJanela()) return;
    setPauseSession(session);
    setPauseReason('');
    setPausePassword('');
  };

  const handleConfirmPause = async () => {
    if (!pauseSession || !pauseReason.trim() || !pausePassword) return;
    if (bloqueadoPorJanela()) return;
    try {
      await pauseMutation.mutateAsync({ id: pauseSession.id, reason: pauseReason.trim(), password: pausePassword });
      notify.success('Atividade pausada com sucesso.');
      workSessionsQuery.refetch();
      refreshAtendimentos();
      setPauseSession(null);
      setPauseReason('');
      setPausePassword('');
    } catch (error: any) {
      const message = error?.response?.data?.message;
      if (message) {
        notify.error(Array.isArray(message) ? message.join(' ') : message);
      } else {
        notify.actionError(error, { action: 'update', subject: 'sessao' });
      }
    }
  };

  const handleOpenResumeModal = (session: WorkSession) => {
    if (bloqueadoPorJanela()) return;
    setResumeModalState({ isOpen: true, session, carPlate: session.carPlate });
    setResumePassword('');
  };

  const handleConfirmResume = async () => {
    if (!resumeModalState || !resumePassword) return;
    if (bloqueadoPorJanela()) return;
    setResumePending(true);
    try {
      await resumeMutation.mutateAsync({ id: resumeModalState.session.id, password: resumePassword });
      notify.success('Atividade retomada com sucesso.');
      workSessionsQuery.refetch();
      refreshAtendimentos();
      setResumeModalState(null);
      setResumePassword('');
    } catch (error: any) {
      const message = error?.response?.data?.message;
      if (message) {
        notify.error(Array.isArray(message) ? message.join(' ') : message);
      } else {
        notify.actionError(error, { action: 'update', subject: 'sessao' });
      }
    } finally {
      setResumePending(false);
    }
  };

  return (
    <div
      className="Frame1000004012"
      style={{
        height: loading ? '100vh' : 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: loading ? 'center' : 'space-between',
        alignItems: loading ? 'center' : 'flex-start',
        backgroundColor: loading ? '#808080' : 'transparent',
      }}
    >
      {loading && <FullScreenLoader />}

      {!loading && (
        <div className="ConteDo controle-producao-page recepcao-conte-do" style={{ paddingBottom: '40px' }}>

          {/* Header Row (Title & Search) */}
          <div className="recepcao-header-row">
            <div className="controle-producao-title-container">
              {isCollaboratorView && userDisplayName && (
                <div className="controle-producao-welcome-subtitle">
                  Olá, <span className="controle-producao-welcome-name">{userDisplayName}</span>
                </div>
              )}
              <div className="controle-producao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isCollaboratorView ? 'Acompanhamento de Veículos' : 'Tempo e SLA'}
                <button
                  type="button"
                  className="help-circle-btn"
                  title="Como Funciona"
                  onClick={() => setIsHelpOpen(true)}
                >
                  ?
                </button>
              </div>
            </div>

            {isCollaboratorView && myRejections.length > 0 && (
              <div className="collaborator-rejections-wrapper" ref={rejectionsPanelRef}>
                <div
                  className="collaborator-rejections-trigger"
                  onClick={() => setRejectionsOpen((o) => !o)}
                >
                  <div className="collaborator-rejections-trigger-left">
                    <span className="collaborator-rejections-dot" />
                    <span className="collaborator-rejections-title">Horas reprovadas</span>
                    <span className="collaborator-rejections-count">{myRejections.length}</span>
                  </div>
                  <span className={`collaborator-rejections-chevron ${rejectionsOpen ? 'open' : ''}`}>▼</span>
                </div>

                {rejectionsOpen && (
                  <div className="collaborator-rejections-dropdown">
                    {myRejections.map((session) => (
                      <div key={session.id} className="collaborator-notification-row">
                        <div>
                          <div className="collaborator-notification-plate">
                            {session.carPlate} — {session.carModel}
                          </div>
                          <div className="collaborator-notification-info">
                            Esteira: <strong>{session.sector}</strong> · Horas: <strong>{formatDuration(session.durationInSeconds)}</strong>
                          </div>
                          {session.rejectionReason && (
                            <div className="collaborator-notification-reason">
                              Motivo: {session.rejectionReason}
                            </div>
                          )}
                          <div className="collaborator-notification-subtext">
                            Reprovado por {session.rejectedByName || 'Chefe de Oficina'}.{' '}
                            {session.interruptedReason
                              ? 'O serviço foi encerrado pelo Chefe de Oficina.'
                              : ''}
                          </div>
                        </div>
                        <button
                          disabled={dismissingRejection === session.id}
                          onClick={async (e) => {
                            e.stopPropagation();
                            setDismissingRejection(session.id);
                            try {
                              await acknowledgeRejection(session.id);
                              setMyRejections((prev) => prev.filter((r) => r.id !== session.id));
                            } catch (error) {
                              // Sem sinal, o botao "Entendido" simplesmente nao fazia nada.
                              notify.actionError(error, { action: 'delete', subject: 'o aviso de horas reprovadas' });
                            } finally {
                              setDismissingRejection(null);
                            }
                          }}
                          className="collaborator-notification-btn"
                        >
                          {dismissingRejection === session.id ? '...' : 'Entendido'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {isCollaboratorView && interruptedNotifs.length > 0 && (
              <div className="collaborator-rejections-wrapper" ref={interruptedPanelRef}>
                <div
                  className="collaborator-rejections-trigger"
                  style={{ borderColor: '#f97316' }}
                  onClick={() => setInterruptedOpen((o) => !o)}
                >
                  <div className="collaborator-rejections-trigger-left">
                    <span className="collaborator-rejections-dot" style={{ background: '#f97316' }} />
                    <span className="collaborator-rejections-title">Sessões encerradas</span>
                    <span className="collaborator-rejections-count" style={{ background: '#f97316' }}>{interruptedNotifs.length}</span>
                  </div>
                  <span className={`collaborator-rejections-chevron ${interruptedOpen ? 'open' : ''}`}>▼</span>
                </div>

                {interruptedOpen && (
                  <div className="collaborator-rejections-dropdown">
                    {interruptedNotifs.map((notif) => (
                      <div key={notif.id} className="collaborator-notification-row">
                        <div>
                          <div className="collaborator-notification-plate">{notif.carPlate}</div>
                          <div className="collaborator-notification-subtext">{notif.message}</div>
                        </div>
                        <button
                          disabled={dismissingInterrupted === notif.id}
                          onClick={async (e) => {
                            e.stopPropagation();
                            setDismissingInterrupted(notif.id);
                            try {
                              await markNotificationAsRead(notif.id);
                              setInterruptedNotifs((prev) => prev.filter((n) => n.id !== notif.id));
                            } catch (error) {
                              // Sem sinal, o botao "Entendido" simplesmente nao fazia nada.
                              notify.actionError(error, { action: 'delete', subject: 'o aviso de sessão encerrada' });
                            } finally {
                              setDismissingInterrupted(null);
                            }
                          }}
                          className="collaborator-notification-btn"
                        >
                          {dismissingInterrupted === notif.id ? '...' : 'Entendido'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {(!isCollaboratorView || activeTab === 'historico') && (
              <div className="recepcao-search-bar">
                <div className="ItemSearch" style={{ cursor: 'pointer' }} onClick={() => { setSearchQuery(searchInput); setPage(1); }}>
                  <img
                    className="imgIcon"
                    src={SearchIcon}
                    alt="Ícone de busca"
                    width="20"
                    height="20"
                  />
                </div>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setSearchQuery(searchInput); setPage(1); } }}
                  placeholder="Pesquise por placa, modelo ou nº do evento"
                  className="recepcao-search-input"
                />
              </div>
            )}
          </div>

          {/* Tabs for collaborator */}
          {isCollaboratorView && (
            <div className="controle-producao-tabs">
              <button
                className={`controle-producao-tab ${activeTab === 'veiculos' ? 'active' : ''}`}
                onClick={() => setActiveTab('veiculos')}
              >
                Meus Veículos
              </button>
              <button
                className={`controle-producao-tab ${activeTab === 'historico' ? 'active' : ''}`}
                onClick={() => setActiveTab('historico')}
              >
                Meu Histórico de Tempos
              </button>
            </div>
          )}

          {(!isCollaboratorView || activeTab === 'historico') && (
            <>
              {/* Toolbar with filter buttons */}
              <div className="VisualizarOrdenar">
                <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex', flexWrap: 'wrap' }}>
                  {isChefeView && (
                    <div className="CtaBig MouseHover" onClick={() => setIsCollaboratorModalOpen(true)}>
                      <div className="Frame1000003987">
                        <img src={PersonIcon} alt="Colaboradores" width="20" height="20" />
                        <div className="ComponentName">Colaboradores</div>
                      </div>
                    </div>
                  )}

                  <div className="CtaBig MouseHover" onClick={handleOpenFiltersModal}>
                    <div className="Frame1000003987">
                      <img src={FilterIcon} alt="Filtrar" width="19.8" height="19.8" />
                      <div className="ComponentName">Filtrar</div>
                    </div>
                  </div>

                  <div className="CtaBig MouseHover" onClick={handleClearFilters}>
                    <div className="Frame1000003987">
                      <img src={CleanIcon} alt="Limpar" width="20" height="20" />
                      <div className="ComponentName" style={{ color: 'var(--tema-texto, #1a1c1b)' }}>Limpar</div>
                    </div>
                  </div>

                  <div className="CtaBig MouseHover" onClick={handleOpenExportModal}>
                    <div className="Frame1000003987">
                      <img src={SaveIcon} alt="Exportar CSV" width="20" height="20" />
                      <div className="ComponentName">Exportar CSV</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="QuantidadeDeCarros">
                <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex', flexWrap: 'wrap' }}>
                  <div className="CtaQuantidadeDeCarros">
                    <div className="Frame1000003987">
                      <div className="ComponentName">
                        {`${startIndex}-${endIndex} de ${total} atendimentos`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {isCollaboratorView && activeTab === 'veiculos' && (
            <div className="controle-producao-operational-section" style={{ marginBottom: 28, marginTop: 12 }}>
              {/* at-48b: aparece e desaparece sozinho no horário da janela (timer na borda). */}
              {acoesBloqueadasPorPausa && janelaAtiva && (
                <div className="pausa-automatica-banner">
                  <strong>Pausa automática em andamento — {rotuloJanela}.</strong>
                  <span>
                    As ações de atividade ficam bloqueadas até {janelaAtiva.horaFim}. Esse tempo é
                    descontado do tempo trabalhado automaticamente, sem você precisar registrar.
                  </span>
                </div>
              )}

              {/* at-53: fora do expediente. A janela tem precedência quando as duas valem. */}
              {acoesBloqueadasPorPausa && !janelaAtiva && foraDoExpediente && (
                <div className="pausa-automatica-banner">
                  <strong>{foraDoExpediente.descricao}.</strong>
                  <span>
                    As ações de atividade ficam bloqueadas fora do horário de funcionamento. O tempo
                    fora do expediente é descontado do tempo trabalhado automaticamente.
                  </span>
                </div>
              )}

              <div className="controle-producao-cards-grid-unified">
                {operationalCars.length === 0 ? (
                  <div className="controle-producao-empty-cards">
                    Nenhum veículo operacional atribuído a você no momento.
                  </div>
                ) : (
                  operationalCars.map((car) => {
                    const activeSession = activeSessionByCar[car.id];
                    return (
                      <div className="Frame1000004033 vehicleCardWrap" key={car.id}>
                        <div
                          className={`CardPainelGeral vehicleCard ${activeSession ? 'active-timer-card' : ''}`}
                        >
                          <div className="TTuloPlaca">
                            <div className="Placa">{car.plate}</div>
                            <div className="Frame1000004065">
                              <div className={`ClasseDoCarroPreenchido ${car.carClass?.replace(/\s/g, '') || ''}`}>
                                <img src={CarWhite} alt="Galeria" />
                                <div className="CarClass">{car.carClass}</div>
                              </div>
                            </div>
                          </div>
                          <div className="CamposBoxCarro">
                            <div className="TTuloPlaca">
                              <div className="ModeloCor">{car.model} - {car.color}</div>
                              <div className="Frame1000004065">
                                {(car.tags ?? []).some((t: any) => t.valor) && (
                                  <div className={`ClasseDoCarroPreenchido ${car.carClass?.replace(/\s/g, '') || ''}`}>
                                    <div className="WaitingReason">
                                      {(car.tags ?? []).filter((t: any) => t.valor).map((t: any) => t.valor).join(' | ')}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="CamposBox">
                              <div className="CamposBoxTitulo">Dias para autorização:</div>
                              <div className="CamposBoxTexto">{car.daysToAuthorize !== null ? car.daysToAuthorize : 'N/A'}</div>
                            </div>
                            <div className="CamposBox">
                              <div className="CamposBoxTitulo">Dias em oficina:</div>
                              <div className="CamposBoxTexto">{car.daysInShop !== null ? car.daysInShop : 'N/A'}</div>
                            </div>
                            <div className="CamposBox">
                              <div className="CamposBoxTitulo">Previsão de entrega:</div>
                              <div className="CamposBoxTexto">{car.deliveryForecast !== null ? car.deliveryForecast : 'N/A'}</div>
                            </div>
                            <div className="CamposBox">
                              <div className="CamposBoxTitulo">Previsão de dias no setor:</div>
                              <div className="CamposBoxTexto">{car.estimatedDaysInStatus !== null ? car.estimatedDaysInStatus : 'N/A'}</div>
                            </div>
                          </div>

                          <div className="Frame1000004049">
                            <div className="CompraDePeAs vehicleCard__section">
                              <div className="CompraDePeAsTitulo">Esteiras</div>
                              <div className="Etapas vehicleCard__chips vehicleCard__chips--lanes">
                                {statusIconsHistory[car.id]
                                  ?.filter((status) => {
                                    if (status.name === 'AGUARDANDO_PROXIMA_ETAPA') {
                                      return car.status === 'AGUARDANDO_PROXIMA_ETAPA';
                                    }
                                    return true;
                                  })
                                  ?.map((status) => {
                                    const planned = plannedStatusesByCarId[car.id];
                                    const isCurrentStatus = status.name === car.esteiraId || status.chave === car.status;
                                    const isImplicit = IMPLICIT_STATUSES.includes(status.name) || (status.chave && IMPLICIT_STATUSES.includes(status.chave));
                                    const isDisabledByPlanning = !isCurrentStatus && !isImplicit && !status.approved && planned !== undefined && !planned.includes(status.name) && (status.chave === undefined || !planned.includes(status.chave));

                                    // at-46: serviço inacabado ganha chip próprio (roxo) e tem
                                    // precedência — é a pendência que o colaborador precisa ver.
                                    const temServicoInacabado = (car.servicosInacabados ?? []).some(
                                      (s: ServicoInacabado) =>
                                        s.estado === 'PAUSADA' && s.esteiraId === status.name,
                                    );

                                    let statusColorClass = 'red';
                                    if (temServicoInacabado) {
                                      statusColorClass = 'purple';
                                    } else if (isCurrentStatus) {
                                      statusColorClass = 'orange';
                                    } else if (status.approved) {
                                      statusColorClass = 'green';
                                    } else if (isImplicit) {
                                      statusColorClass = 'blue';
                                    }

                                    return (
                                      <div
                                        key={status.name}
                                        className={`Etapa chip chip--${statusColorClass} ${statusColorClass} ${isDisabledByPlanning ? 'chip--disabled' : ''}`}
                                        title={status.label || status.name}
                                      >
                                        <div
                                          className="EtapaTexto text-center"
                                          style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '50px',
                                          }}
                                        >
                                          {status.label || status.name}
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          </div>

                          <div style={{ width: '100%', marginTop: '12px', display: 'flex', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
                            {!activeSession ? (
                              <button
                                type="button"
                                className="op-card-btn start-btn"
                                onClick={() => handleOpenStartModal(car)}
                                style={{ flex: 1 }}
                                disabled={acoesBloqueadasPorPausa}
                                title={
                                  acoesBloqueadasPorPausa ? tooltipBloqueio : undefined
}
                              >
                                {/* at-99: "Retomar" vale para serviço pausado em QUALQUER esteira
                                    ativa do veículo — a etapa ou uma aberta em paralelo. Antes
                                    olhava só `car.esteiraId`, e o serviço pausado da paralela
                                    aparecia como "Iniciar Atividade", como se nunca tivesse
                                    começado. */}
                                {(car.servicosInacabados ?? []).some(
                                  (s: ServicoInacabado) =>
                                    s.estado === 'PAUSADA' &&
                                    (s.esteiraId === car.esteiraId ||
                                      (car.esteirasParalelas ?? []).some(
                                        (p: any) => p.esteiraId === s.esteiraId,
                                      )),
                                )
                                  ? 'Retomar Serviço'
                                  : 'Iniciar Atividade'}
                              </button>
                            ) : activeSession.status === 'PAUSED' ? (
                              <>
                                <button
                                  type="button"
                                  className="op-card-btn start-btn"
                                  onClick={() => handleOpenResumeModal(activeSession)}
                                  style={{ flex: 1 }}
                                  disabled={acoesBloqueadasPorPausa}
                                  title={
                                    acoesBloqueadasPorPausa
                                      ? tooltipBloqueio
                                      : undefined
                                  }
                                >
                                  Retomar
                                </button>
                                <button
                                  type="button"
                                  className="op-card-btn stop-btn"
                                  onClick={() => handleInitiateStop(activeSession, car.id)}
                                  style={{ flex: 1 }}
                                  disabled={acoesBloqueadasPorPausa}
                                  title={
                                    acoesBloqueadasPorPausa
                                      ? tooltipBloqueio
                                      : undefined
                                  }
                                >
                                  Finalizar
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="op-card-btn pause-btn"
                                  onClick={() => handleOpenPauseModal(activeSession)}
                                  style={{ flex: 1 }}
                                  disabled={acoesBloqueadasPorPausa}
                                  title={
                                    acoesBloqueadasPorPausa
                                      ? tooltipBloqueio
                                      : undefined
                                  }
                                >
                                  Pausar
                                </button>
                                <button
                                  type="button"
                                  className="op-card-btn stop-btn"
                                  onClick={() => handleInitiateStop(activeSession, car.id)}
                                  style={{ flex: 1 }}
                                  disabled={acoesBloqueadasPorPausa}
                                  title={
                                    acoesBloqueadasPorPausa
                                      ? tooltipBloqueio
                                      : undefined
                                  }
                                >
                                  Finalizar
                                </button>
                              </>
                            )}
                          </div>

                          {/* at-99: por que este veículo pede escolha de esteira ao iniciar. */}
                          <FaixaDeParalelas
                            esteiraPrincipalNome={labelDe(car.status) || car.esteiraId}
                            esteirasParalelas={car.esteirasParalelas}
                          />

                          {/* at-89: sub-tarefas da esteira atual. O colaborador marca a qualquer
                              momento (não só ao finalizar) — é o que sustenta a resposta ao
                              associado sobre o que já foi feito. Some quando a esteira não tem
                              sub-tarefas cadastradas. */}
                          <SubtarefasDoVeiculo
                            carId={car.id}
                            bloqueado={acoesBloqueadasPorPausa}
                            motivoBloqueio={acoesBloqueadasPorPausa ? tooltipBloqueio : null}
                            onProgressoAtualizado={(p) => registrarProgressoSubtarefas(car.id, p)}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Main Table Card */}
          {(!isCollaboratorView || activeTab === 'historico') && (
            <div className="controle-producao-card">
              <div className="TabelaScroll" ref={tableScrollRef}>
                <div className="TabelaContent controle-producao-content" style={{ width: tableWidth, minWidth: tableWidth }}>
                  <div className="Tabela TabelaHeader">
                    <div className="Placa">
                      <div className="TTulo controle-producao-grid" style={{ gridTemplateColumns: tableColumns }}>
                        {headers.map((header, index) => (
                          <div
                            key={header}
                            className="Opcoes"
                            ref={(el) => {
                              headerRefs.current[index] = el;
                            }}
                          >
                            {header}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {atendimentos.length === 0 && (
                    <div className="Tabela" style={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                      <div className="Item" style={{ justifyContent: 'center' }}>
                        <div className="Data">Nenhum atendimento encontrado para os filtros informados.</div>
                      </div>
                    </div>
                  )}

                  {atendimentos.map((atendimento) => (
                    <div className="Tabela controle-producao-grid" key={atendimento.carId} style={{ gridTemplateColumns: tableColumns }}>
                      <div className="Item">
                        <div
                          className="Data"
                          onClick={() => setSelectedAtendimentoCarId(atendimento.carId)}
                          style={{
                            cursor: 'pointer',
                            color: '#ea5d0e',
                            fontWeight: '600',
                            textDecoration: 'underline',
                          }}
                        >
                          {atendimento.plate || '-'}
                        </div>
                      </div>
                      <div className="Item"><div className="Data">{atendimento.model || '-'}</div></div>
                      <div className="Item"><div className="Data">{atendimento.entryDate ? new Date(atendimento.entryDate).toLocaleDateString('pt-BR') : '-'}</div></div>
                      <div className="Item"><div className="Data">{formatDuration(atendimento.totalWorkedSeconds)}</div></div>
                      <div className="Item"><div className="Data">{atendimento.activitiesCount}</div></div>
                      <div className="Item">
                        {/* at-46: a coluna distingue horas aguardando aprovação de serviço
                            inacabado — antes "pendências" só significava horas. */}
                        {atendimento.pendingCount > 0 || (atendimento.servicosInacabados ?? []).some((s: ServicoInacabado) => s.estado === 'PAUSADA') ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                            {atendimento.pendingCount > 0 && (
                              <div className="badge badge-approval approval-pending">
                                {atendimento.pendingCount} hora(s)
                              </div>
                            )}
                            {(atendimento.servicosInacabados ?? []).some((s: ServicoInacabado) => s.estado === 'PAUSADA') && (
                              <span
                                className="servico-inacabado-badge"
                                title={(atendimento.servicosInacabados ?? [])
                                  .filter((s: ServicoInacabado) => s.estado === 'PAUSADA')
                                  .map((s: ServicoInacabado) => s.esteiraNome || s.esteiraId)
                                  .join(', ')}
                              >
                                Serviço inacabado
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="Data">-</div>
                        )}
                      </div>
                      <div className="Item">
                        <div className={`badge ${atendimento.esteiraChave === ESTEIRA.ENTREGA ? 'status-finished' : 'status-in-progress'}`}>
                          {esteiraAtualLabel(atendimento)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {total > 0 && renderPagination(page, totalPages, setPage)}
            </div>
          )}
        </div>
      )}

      {/* Collaborator Modal */}
      {isCollaboratorModalOpen && (
        <div className="controle-producao-modal-overlay" onClick={() => setIsCollaboratorModalOpen(false)}>
          <div className="controle-producao-modal" onClick={(e) => e.stopPropagation()}>
            <div className="controle-producao-modal-header">
              <h3>Selecionar Colaborador</h3>
              <button className="close-button" onClick={() => setIsCollaboratorModalOpen(false)}>&times;</button>
            </div>
            <div className="controle-producao-modal-body">
              <div className="controle-producao-modal-search-wrapper">
                <img className="search-icon-inside" src={SearchIcon} alt="" width="18" height="18" />
                <input
                  className="controle-producao-modal-search"
                  type="text"
                  placeholder="Pesquise por nome ou usuário..."
                  value={collaboratorSearchInput}
                  onChange={(e) => {
                    setCollaboratorSearchInput(e.target.value);
                    setCollaboratorPage(1);
                  }}
                />
              </div>

              {loadingCollaborators ? (
                <div className="controle-producao-modal-loading">
                  <div className="spinner"></div>
                  <p>Buscando colaboradores...</p>
                </div>
              ) : (
                <div className="controle-producao-modal-list">
                  <div
                    className={`controle-producao-modal-option ${!collaboratorIdFilter ? 'is-selected' : ''}`}
                    onClick={() => {
                      setCollaboratorIdFilter('');
                      setPage(1);
                      setIsCollaboratorModalOpen(false);
                    }}
                  >
                    <div className="controle-producao-modal-avatar all-users">*</div>
                    <div className="controle-producao-modal-info">
                      <span className="controle-producao-modal-name">Todos os colaboradores</span>
                      <span className="controle-producao-modal-username">Sem filtro de colaborador</span>
                    </div>
                    {!collaboratorIdFilter && (
                      <div className="controle-producao-modal-selected-indicator">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                    )}
                  </div>

                  {paginatedCollaborators.map((c) => {
                    const initials = c.fullName
                      ? c.fullName
                        .split(' ')
                        .slice(0, 2)
                        .map((n: string) => n[0])
                        .join('')
                        .toUpperCase()
                      : '?';
                    const isSelected = collaboratorIdFilter === c.id;

                    return (
                      <div
                        key={c.id}
                        className={`controle-producao-modal-option ${isSelected ? 'is-selected' : ''}`}
                        onClick={() => {
                          setCollaboratorIdFilter(c.id);
                          setPage(1);
                          setIsCollaboratorModalOpen(false);
                        }}
                      >
                        <div className="controle-producao-modal-avatar">{initials}</div>
                        <div className="controle-producao-modal-info">
                          <span className="controle-producao-modal-name">{c.fullName || c.username || 'Colaborador'}</span>
                          <span className="controle-producao-modal-username">{c.username || 'colaborador'}</span>
                        </div>
                        {isSelected && (
                          <div className="controle-producao-modal-selected-indicator">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {filteredCollaborators.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--tema-texto-3, #6b7280)', fontFamily: 'AmpleSoft-Regular, sans-serif' }}>
                      Nenhum colaborador encontrado.
                    </div>
                  )}
                </div>
              )}

              <div className="controle-producao-modal-pagination">
                <div
                  className={`controle-producao-modal-page-btn ${collaboratorPage === 1 ? 'disabled' : ''}`}
                  onClick={() => collaboratorPage > 1 && setCollaboratorPage(collaboratorPage - 1)}
                  style={{ cursor: collaboratorPage === 1 ? 'not-allowed' : 'pointer' }}
                >
                  <img
                    className="imgIcon"
                    src={collaboratorPage === 1 ? ArrowLeftGray : ArrowLeftOrange}
                    alt="Seta para Esquerda"
                    width="24"
                    height="24"
                  />
                  <div
                    className="Anterior"
                    style={{ color: collaboratorPage === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)', fontSize: '14px', fontWeight: 600 }}
                  >
                    Anterior
                  </div>
                </div>
                <span style={{ fontSize: '13px', color: 'var(--tema-texto-2, #4b5563)', fontFamily: 'AmpleSoft-Bold, sans-serif', fontWeight: 700 }}>
                  Página {collaboratorPage} de {totalCollaboratorPages}
                </span>
                <div
                  className={`controle-producao-modal-page-btn ${collaboratorPage >= totalCollaboratorPages ? 'disabled' : ''}`}
                  onClick={() => collaboratorPage < totalCollaboratorPages && setCollaboratorPage(collaboratorPage + 1)}
                  style={{ cursor: collaboratorPage >= totalCollaboratorPages ? 'not-allowed' : 'pointer' }}
                >
                  <div
                    className="PrXima"
                    style={{ color: collaboratorPage >= totalCollaboratorPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)', fontSize: '14px', fontWeight: 600 }}
                  >
                    Próxima
                  </div>
                  <img
                    className="imgIcon"
                    src={collaboratorPage >= totalCollaboratorPages ? ArrowRightGray : ArrowRightOrange}
                    alt="Seta para Direita"
                    width="24"
                    height="24"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Modal */}
      {isFiltersModalOpen && (
        <div className="controle-producao-modal-overlay" onClick={() => setIsFiltersModalOpen(false)}>
          <div className="controle-producao-modal controle-producao-filters-modal" onClick={(e) => e.stopPropagation()}>
            <div className="controle-producao-modal-header">
              <h3>Filtros Avançados</h3>
              <button className="close-button" onClick={() => setIsFiltersModalOpen(false)}>&times;</button>
            </div>
            <div className="controle-producao-filters-modal-body">
              <div className="controle-producao-filter-field">
                <label>Esteira atual</label>
                <select
                  value={tempFilters.esteiraId}
                  onChange={(e) => setTempFilters(prev => ({ ...prev, esteiraId: e.target.value }))}
                >
                  <option value="">Todas as esteiras</option>
                  {esteirasAtivas.map((esteira) => (
                    <option key={esteira.id} value={esteira.id}>{esteira.nome}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="controle-producao-filter-field">
                  <label>Entrada de</label>
                  <input
                    type="date"
                    value={tempFilters.entryStartDate}
                    onChange={(e) => setTempFilters(prev => ({ ...prev, entryStartDate: e.target.value }))}
                  />
                </div>

                <div className="controle-producao-filter-field">
                  <label>Entrada até</label>
                  <input
                    type="date"
                    value={tempFilters.entryEndDate}
                    onChange={(e) => setTempFilters(prev => ({ ...prev, entryEndDate: e.target.value }))}
                  />
                </div>
              </div>

              <div className="controle-producao-filter-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  id="hasPendingFilter"
                  checked={tempFilters.hasPending}
                  onChange={(e) => setTempFilters(prev => ({ ...prev, hasPending: e.target.checked }))}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <label htmlFor="hasPendingFilter" style={{ cursor: 'pointer', marginBottom: 0 }}>
                  Somente atendimentos com horas pendentes de aprovação
                </label>
              </div>
            </div>
            <div className="controle-producao-filters-modal-actions">
              <button className="controle-producao-btn-clear" onClick={() => setIsFiltersModalOpen(false)}>
                Cancelar
              </button>
              <button className="controle-producao-btn-apply" onClick={handleApplyFilters}>
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Next Sector Modal */}
      {nextSectorSession && (
        <div className="controle-producao-modal-overlay" onClick={() => { setNextSectorSession(null); setNextSectorCarId(null); }}>
          <div className="controle-producao-modal controle-producao-next-sector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="controle-producao-modal-header">
              <h3>Para qual esteira o veículo vai?</h3>
              <button className="close-button" onClick={() => { setNextSectorSession(null); setNextSectorCarId(null); }}>&times;</button>
            </div>
            <div className="controle-producao-modal-body">
              <div style={{ fontSize: '14px', color: 'var(--tema-texto-2, #475569)', marginBottom: '4px' }}>
                Veículo: <strong>{nextSectorSession.carPlate}</strong> — {nextSectorSession.carModel}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--tema-texto-3, #6b7280)', marginBottom: '12px' }}>
                Setor atual: <strong>{labelDe(nextSectorSession.sector) || nextSectorSession.sector}</strong>
              </div>

              {/* at-46: desfecho do serviço da esteira. A escolha é explícita e cada opção
                  descreve, antes de confirmar, o que vai acontecer com o veículo. */}
              <div className="servico-desfecho-bloco">
                <div className="servico-desfecho-pergunta">
                  O serviço de <strong>{labelDe(nextSectorSession.sector) || nextSectorSession.sector}</strong> foi concluído?
                </div>
                {/* Classe própria (sem max-height/scroll) — reusar a lista de esteiras aqui
                    criaria um segundo container rolável dentro do corpo do modal. */}
                <div className="servico-desfecho-opcoes">
                  <div
                    className={`controle-producao-next-sector-option ${servicoConcluido === true ? 'selected' : ''}`}
                    onClick={() => setServicoConcluido(true)}
                  >
                    <div className="next-sector-option-name">Sim, serviço concluído</div>
                    <div className="next-sector-option-desc">
                      A etapa se encerra para o veículo
                    </div>
                    {servicoConcluido === true && <div className="next-sector-check">✓</div>}
                  </div>
                  <div
                    className={`controle-producao-next-sector-option ${servicoConcluido === false ? 'selected' : ''}`}
                    onClick={() => setServicoConcluido(false)}
                  >
                    <div className="next-sector-option-name">Não, ficou inacabado</div>
                    <div className="next-sector-option-desc">
                      O serviço continua pendente e pode ser retomado depois
                    </div>
                    {servicoConcluido === false && <div className="next-sector-check">✓</div>}
                  </div>
                </div>

                {/* at-89: avisa, NÃO bloqueia. O que ficou sem marcar continua registrado como
                    pendente e aparece para o Chefe — mas declarar o serviço concluído sem marcar
                    nada costuma ser esquecimento, não decisão. */}
                {servicoConcluido === true &&
                  (pendenciasSubtarefas[nextSectorSession.carId] ?? 0) > 0 && (
                    <p className="servico-desfecho-aviso" style={{ marginTop: '12px' }}>
                      Ainda há{' '}
                      <strong>
                        {pendenciasSubtarefas[nextSectorSession.carId]} sub-tarefa
                        {pendenciasSubtarefas[nextSectorSession.carId] > 1 ? 's' : ''} sem marcar
                      </strong>{' '}
                      nesta etapa. Você pode finalizar assim mesmo — elas continuam registradas como
                      pendentes e o Chefe de Oficina consegue ver. Se já fez alguma delas, marque
                      antes de confirmar.
                    </p>
                  )}

                {servicoConcluido === false && (
                  // at-134 (ADR 0065): o motivo da parada (lista) + o detalhe. Com a lista vazia,
                  // o componente mostra o campo de texto livre de antes.
                  <MotivoDaParada
                    idPrefixo="servico-pausa"
                    motivos={motivosDaParada}
                    estado={estadoDosMotivos}
                    motivoId={motivoParadaId}
                    onMotivo={setMotivoParadaId}
                    detalhe={pausaObservacao}
                    onDetalhe={setPausaObservacao}
                  />
                )}

                {servicoConcluido !== null && (
                  <p className="servico-desfecho-aviso">
                    {servicoConcluido
                      ? 'Ao confirmar, suas horas vão para a aprovação do Chefe de Oficina. Com a aprovação, se esta for a última atividade do veículo nesta etapa, ele vai para Aguardando próxima etapa e os colaboradores são desvinculados.'
                      : `Ao confirmar, suas horas vão para a aprovação do Chefe de Oficina, mas o serviço de ${labelDe(nextSectorSession.sector) || nextSectorSession.sector} fica registrado como inacabado: o veículo pode seguir para outra esteira e voltar depois para você retomar de onde parou. Ninguém é desvinculado e o Chefe de Oficina é avisado. Se outro colaborador ainda estiver trabalhando neste veículo, o registro vale quando o último finalizar.`}
                  </p>
                )}
              </div>

              {loadingActivities ? (
                <div className="controle-producao-modal-loading">
                  <div className="spinner"></div>
                  <p>Carregando esteiras disponíveis...</p>
                </div>
              ) : (
                <div className="controle-producao-next-sector-list">
                  <div
                    className={`controle-producao-next-sector-option ${selectedNextSector === '' ? 'selected' : ''}`}
                    onClick={() => setSelectedNextSector('')}
                  >
                    <div className="next-sector-option-name">Não alterar esteira</div>
                    <div className="next-sector-option-desc">Apenas finaliza a atividade</div>
                    {selectedNextSector === '' && <div className="next-sector-check">✓</div>}
                  </div>
                  {availableActivities
                    .filter((activity) => {
                      const isCurrentStatus = activity.chave === nextSectorSession.sector || activity.nome === nextSectorSession.sector;
                      const wasApproved = statusIconsHistory[nextSectorCarId || '']?.find((h: any) => h.name === activity.status)?.approved || false;
                      const isPlanned = plannedStatusesByCarId[nextSectorCarId || '']?.includes(activity.status) || false;
                      // Só sistema (Padrão), previstas, realizadas ou a atual.
                      return activity.sistema || isPlanned || wasApproved || isCurrentStatus;
                    })
                    .map((activity) => {
                    const isEntrega = activity.chave === 'ENTREGA';
                    const isBlocked = isEntrega && userType === 'COLABORADOR';

                    const isCurrentStatus = activity.chave === nextSectorSession.sector || activity.nome === nextSectorSession.sector;
                    const wasApproved = (statusIconsHistory[nextSectorCarId || '']?.find((h: any) => h.name === activity.status)?.approved || false) && activity.chave !== 'AGUARDANDO_PROXIMA_ETAPA';
                    const isPlanned = (plannedStatusesByCarId[nextSectorCarId || '']?.includes(activity.status) || false) && activity.chave !== 'AGUARDANDO_PROXIMA_ETAPA';

                    let badgeText: string | null = activity.sistema ? 'Padrão' : null;
                    let badgeClass = 'badge-neutral';
                    let leftBorderColor = '#94a3b8';

                    if (isCurrentStatus) {
                      badgeText = 'Atual';
                      badgeClass = 'badge-orange';
                      leftBorderColor = '#ea5d0e';
                    } else if (wasApproved) {
                      badgeText = 'Realizada';
                      badgeClass = 'badge-green';
                      leftBorderColor = '#10b981';
                    } else if (isPlanned) {
                      badgeText = 'Prevista';
                      badgeClass = 'badge-red';
                      leftBorderColor = '#ef4444';
                    }

                    const showBadge = badgeText !== null && (isCurrentStatus || activity.chave !== 'AGUARDANDO_PROXIMA_ETAPA');

                    return (
                      <div
                        key={activity.status}
                        className={`controle-producao-next-sector-option ${selectedNextSector === activity.status ? 'selected' : ''} ${isBlocked ? 'disabled' : ''}`}
                        style={{
                          borderLeft: `4px solid ${leftBorderColor}`,
                          ...(isBlocked ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' } : {})
                        }}
                        onClick={() => {
                          if (!isBlocked) {
                            setSelectedNextSector(activity.status);
                          }
                        }}
                      >
                        <div className="next-sector-option-name">
                          {activity.esteira?.nome || activity.statusLabel || activity.status}
                        </div>
                        {showBadge && (
                          <span className={`next-sector-status-badge ${badgeClass}`}>
                            {badgeText}
                          </span>
                        )}
                        {selectedNextSector === activity.status && <div className="next-sector-check">✓</div>}
                      </div>
                    );
                  })}
                  {availableActivities.length === 0 && (
                    <div style={{ color: 'var(--tema-texto-3, #6b7280)', fontSize: '13px', padding: '12px 0' }}>
                      Nenhuma esteira planejada para este veículo.
                    </div>
                  )}
                </div>
              )}

              <div className="controle-producao-filter-field" style={{ marginTop: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--tema-texto-2, #334155)' }}>
                  Sua senha <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="password"
                  className="controle-producao-modal-input"
                  style={modalInputStyle}
                  autoComplete="current-password"
                  placeholder="Digite sua senha para confirmar a finalização"
                  value={stopPassword}
                  onChange={(e) => setStopPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && stopPassword && servicoConcluido !== null && !pendenciaDaParada && !stopMutation.isPending) {
                      handleConfirmStop();
                    }
                  }}
                />
              </div>
              {/* at-134: o botão desabilitado diz POR QUÊ (lista falhou, motivo não escolhido). */}
              {pendenciaDaParada && estadoDosMotivos === 'pronto' && (
                <p className="servico-desfecho-aviso">{pendenciaDaParada}</p>
              )}
            </div>
            <div className="controle-producao-filters-modal-actions">
              <button
                className="controle-producao-btn-clear"
                onClick={() => { setNextSectorSession(null); setNextSectorCarId(null); setStopPassword(''); }}
              >
                Cancelar
              </button>
              <button
                className="controle-producao-btn-apply"
                onClick={handleConfirmStop}
                // at-46: a declaração do desfecho do serviço é obrigatória — o sistema não
                // decide sozinho se a etapa terminou.
                disabled={!stopPassword || servicoConcluido === null || Boolean(pendenciaDaParada) || stopMutation.isPending}
              >
                {stopMutation.isPending ? 'Finalizando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export CSV Modal */}
      {isExportModalOpen && (
        <div className="controle-producao-modal-overlay" onClick={() => setIsExportModalOpen(false)}>
          <div className="controle-producao-modal controle-producao-export-modal" onClick={(e) => e.stopPropagation()}>
            <div className="controle-producao-modal-header">
              <h3>Exportar Relatório CSV</h3>
              <button className="close-button" onClick={() => setIsExportModalOpen(false)}>&times;</button>
            </div>
            <div className="controle-producao-filters-modal-body">

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="controle-producao-filter-field">
                  <label>Data de Início</label>
                  <input type="date" value={exportFilters.startDate || ''} onChange={(e) => setExportFilters(p => ({ ...p, startDate: e.target.value }))} />
                </div>
                <div className="controle-producao-filter-field">
                  <label>Data de Fim</label>
                  <input type="date" value={exportFilters.endDate || ''} onChange={(e) => setExportFilters(p => ({ ...p, endDate: e.target.value }))} />
                </div>
              </div>

              <div className="controle-producao-filter-field">
                <label>Setor (Esteira)</label>
                <select value={exportFilters.sector || ''} onChange={(e) => setExportFilters(p => ({ ...p, sector: e.target.value }))}>
                  <option value="">Todas as esteiras</option>
                  {esteirasAtivas.map((esteira) => (
                    <option key={esteira.id} value={esteira.id}>{esteira.nome}</option>
                  ))}
                </select>
              </div>

              <div className="controle-producao-filter-field">
                <label>Status da Atividade</label>
                <select value={exportFilters.status || ''} onChange={(e) => setExportFilters(p => ({ ...p, status: e.target.value as WorkSessionStatus | '' }))}>
                  <option value="">Todos</option>
                  <option value="IN_PROGRESS">Em andamento</option>
                  <option value="PAUSED">Pausada</option>
                  <option value="FINISHED">Finalizada</option>
                </select>
              </div>

              <div className="controle-producao-filter-field">
                <label>Status de Aprovação</label>
                <select value={exportFilters.approvalStatus || ''} onChange={(e) => setExportFilters(p => ({ ...p, approvalStatus: e.target.value as WorkSessionApprovalStatus | '' }))}>
                  <option value="">Todos</option>
                  <option value="PENDING">Pendente</option>
                  <option value="APPROVED">Aprovado</option>
                  <option value="REJECTED">Reprovada</option>
                </select>
              </div>

              <div className="controle-producao-filter-field">
                <label>Aprovado/Reprovado por</label>
                <select value={exportFilters.approvedById || ''} onChange={(e) => setExportFilters(p => ({ ...p, approvedById: e.target.value || undefined }))}>
                  <option value="">Todos</option>
                  {exportApprovers.map((u) => (
                    <option key={u.id} value={u.id}>{u.fullName || u.username}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="controle-producao-filter-field">
                  <label>Duração mínima (horas)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="Ex: 1"
                    value={exportFilters.minDuration ? String(Number(exportFilters.minDuration) / 3600) : ''}
                    onChange={(e) => setExportFilters(p => ({ ...p, minDuration: e.target.value ? String(Math.round(Number(e.target.value) * 3600)) : '' }))}
                  />
                </div>
                <div className="controle-producao-filter-field">
                  <label>Duração máxima (horas)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="Ex: 8"
                    value={exportFilters.maxDuration ? String(Number(exportFilters.maxDuration) / 3600) : ''}
                    onChange={(e) => setExportFilters(p => ({ ...p, maxDuration: e.target.value ? String(Math.round(Number(e.target.value) * 3600)) : '' }))}
                  />
                </div>
              </div>

              <div className="controle-producao-filter-field">
                <label>Pesquisa (placa, modelo ou colaborador)</label>
                <input
                  type="text"
                  placeholder="Ex: ABC-1234"
                  value={exportFilters.q || ''}
                  onChange={(e) => setExportFilters(p => ({ ...p, q: e.target.value }))}
                />
              </div>

              <div className="controle-producao-filter-field">
                <label>Agrupar por</label>
                <select value={exportFilters.groupBy || ''} onChange={(e) => setExportFilters(p => ({ ...p, groupBy: e.target.value as WorkSessionExportFilters['groupBy'] }))}>
                  <option value="">Sem agrupamento (mais recente primeiro)</option>
                  <option value="sector">Esteira</option>
                  <option value="collaborator">Colaborador</option>
                  <option value="day">Dia</option>
                  <option value="week">Semana</option>
                  <option value="month">Mês</option>
                </select>
              </div>

              <div className="controle-producao-filter-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  id="includeSummary"
                  checked={exportFilters.includeSummary === 'true'}
                  onChange={(e) => setExportFilters(p => ({ ...p, includeSummary: e.target.checked ? 'true' : 'false' }))}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <label htmlFor="includeSummary" style={{ cursor: 'pointer', marginBottom: 0 }}>
                  Incluir resumo de totais por esteira e colaborador
                </label>
              </div>

              {exportFilters.collaboratorId && (
                <div style={{ fontSize: '12px', color: 'var(--tema-texto-3, #6b7280)', padding: '6px 10px', background: 'var(--tema-fundo, #f3f4f6)', borderRadius: '8px' }}>
                  Filtrado pelo colaborador selecionado anteriormente
                </div>
              )}

            </div>
            <div className="controle-producao-filters-modal-actions">
              <button className="controle-producao-btn-clear" onClick={() => setIsExportModalOpen(false)} disabled={isExporting}>
                Cancelar
              </button>
              <button className="controle-producao-btn-apply" onClick={handleExportCsv} disabled={isExporting}>
                {isExporting ? 'Gerando...' : 'Exportar CSV'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal detalhado do atendimento */}
      {selectedAtendimentoCarId && (
        <AtendimentoDetailsModal
          carId={selectedAtendimentoCarId}
          isChefeView={isChefeView}
          onClose={() => setSelectedAtendimentoCarId(null)}
          onChanged={refreshAtendimentos}
        />
      )}
      {/* Pause Modal */}
      {pauseSession && (
        <div className="controle-producao-modal-overlay" onClick={() => { setPauseSession(null); setPauseReason(''); setPausePassword(''); }}>
          <div className="controle-producao-modal" onClick={(e) => e.stopPropagation()}>
            <div className="controle-producao-modal-header">
              <h3>Pausar Atividade</h3>
              <button className="close-button" onClick={() => { setPauseSession(null); setPauseReason(''); setPausePassword(''); }}>&times;</button>
            </div>
            <div className="controle-producao-modal-body">
              <div style={{ fontSize: '14px', color: 'var(--tema-texto-2, #475569)', marginBottom: '16px' }}>
                Veículo: <strong>{pauseSession.carPlate}</strong> — {pauseSession.carModel}
              </div>
              {/* at-47: a tela descreve o resultado antes de confirmar (padrão at-18/at-46). */}
              <p className="servico-desfecho-aviso" style={{ margin: '0 0 16px' }}>
                Ao pausar, o tempo para de contar e você pode <strong>iniciar ou retomar a atividade
                em outro veículo</strong>. Esta atividade fica pausada com você até retomar ou
                finalizar, e as horas só vão para a aprovação do Chefe de Oficina quando você
                finalizar. Se o veículo for movido de esteira enquanto ela estiver pausada, a
                atividade é encerrada automaticamente e as horas vão para aprovação.
              </p>
              <div className="controle-producao-filter-field">
                <label>Justificativa da pausa <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  className="controle-producao-modal-input"
                  style={modalInputStyle}
                  placeholder="Ex: Almoço, saída antecipada..."
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && pauseReason.trim().length >= 3 && pausePassword) handleConfirmPause(); }}
                />
                {pauseReason.trim().length > 0 && pauseReason.trim().length < 3 && (
                  <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>Mínimo 3 caracteres</div>
                )}
              </div>
              <div className="controle-producao-filter-field" style={{ marginTop: '12px' }}>
                <label>Sua senha <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="password"
                  className="controle-producao-modal-input"
                  style={modalInputStyle}
                  autoComplete="current-password"
                  placeholder="Digite sua senha de login"
                  value={pausePassword}
                  onChange={(e) => setPausePassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && pauseReason.trim().length >= 3 && pausePassword) handleConfirmPause(); }}
                />
              </div>
            </div>
            <div className="controle-producao-filters-modal-actions">
              <button className="controle-producao-btn-clear" onClick={() => { setPauseSession(null); setPauseReason(''); setPausePassword(''); }}>
                Cancelar
              </button>
              <button
                className="controle-producao-btn-apply"
                onClick={handleConfirmPause}
                disabled={pauseReason.trim().length < 3 || !pausePassword || pauseMutation.isPending}
              >
                {pauseMutation.isPending ? 'Pausando...' : 'Confirmar Pausa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Start Modal */}
      {startModalState && (
        <div className="controle-producao-modal-overlay" onClick={() => { setStartModalState(null); setStartPassword(''); }}>
          <div className="controle-producao-modal" onClick={(e) => e.stopPropagation()}>
            <div className="controle-producao-modal-header">
              <h3>Confirmar início da atividade</h3>
              <button className="close-button" onClick={() => { setStartModalState(null); setStartPassword(''); }}>&times;</button>
            </div>
            <div className="controle-producao-modal-body">
              <div style={{ fontSize: '14px', color: 'var(--tema-texto-2, #475569)', marginBottom: '16px' }}>
                Veículo: <strong>{startModalState.carPlate}</strong> {startModalState.carModel ? `— ${startModalState.carModel}` : ''}
              </div>

              {/* at-99 (ADR 0050): com esteira aberta em paralelo, o veículo tem mais de um
                  serviço em curso — o colaborador precisa dizer em qual deles está trabalhando,
                  porque é ali que a hora dele é apontada. Com uma esteira só, o campo não
                  aparece e nada muda em relação ao comportamento de sempre. */}
              {startModalState.esteirasDisponiveis.length > 1 && (
                <div className="controle-producao-filter-field">
                  <label>
                    Em qual esteira você vai trabalhar? <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    className="controle-producao-modal-input"
                    style={modalInputStyle}
                    value={startEsteiraId}
                    onChange={(e) => setStartEsteiraId(e.target.value)}
                  >
                    <option value="">Selecione a esteira</option>
                    {startModalState.esteirasDisponiveis.map((esteira) => (
                      <option key={esteira.esteiraId} value={esteira.esteiraId}>
                        {esteira.nome}
                        {esteira.principal ? ' (etapa do veículo)' : ' (aberta em paralelo)'}
                      </option>
                    ))}
                  </select>
                  <p style={{ fontSize: '12px', color: 'var(--tema-texto-3, #64748b)', margin: '6px 0 0', lineHeight: 1.4 }}>
                    Este veículo tem serviço em curso em mais de uma esteira. Escolha a do serviço
                    que <strong>você</strong> vai fazer: suas horas são apontadas nela, e é a
                    especialidade dela que o sistema confere.
                  </p>
                </div>
              )}

              <div className="controle-producao-filter-field">
                <label>Sua senha <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="password"
                  className="controle-producao-modal-input"
                  style={modalInputStyle}
                  autoComplete="current-password"
                  placeholder="Digite sua senha de login"
                  value={startPassword}
                  onChange={(e) => setStartPassword(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (
                      e.key === 'Enter' &&
                      startPassword &&
                      !startPending &&
                      (startModalState.esteirasDisponiveis.length <= 1 || startEsteiraId)
                    ) {
                      handleConfirmStart();
                    }
                  }}
                />
              </div>
            </div>
            <div className="controle-producao-filters-modal-actions">
              <button className="controle-producao-btn-clear" onClick={() => { setStartModalState(null); setStartPassword(''); }}>
                Cancelar
              </button>
              <button
                className="controle-producao-btn-apply"
                onClick={handleConfirmStart}
                disabled={
                  !startPassword ||
                  startPending ||
                  // at-99: com mais de uma esteira em curso, a escolha é obrigatória — enviar
                  // sem ela apontaria a hora na principal por omissão, que pode ser a esteira
                  // errada e é justamente o que a entrega existe para corrigir.
                  (startModalState.esteirasDisponiveis.length > 1 && !startEsteiraId)
                }
              >
                {startPending ? 'Iniciando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume Modal */}
      {resumeModalState && (
        <div className="controle-producao-modal-overlay" onClick={() => { setResumeModalState(null); setResumePassword(''); }}>
          <div className="controle-producao-modal" onClick={(e) => e.stopPropagation()}>
            <div className="controle-producao-modal-header">
              <h3>Confirmar retomada da atividade</h3>
              <button className="close-button" onClick={() => { setResumeModalState(null); setResumePassword(''); }}>&times;</button>
            </div>
            <div className="controle-producao-modal-body">
              <div style={{ fontSize: '14px', color: 'var(--tema-texto-2, #475569)', marginBottom: '16px' }}>
                Veículo: <strong>{resumeModalState.carPlate}</strong> {resumeModalState.session.carModel ? `— ${resumeModalState.session.carModel}` : ''}
              </div>
              <div className="controle-producao-filter-field">
                <label>Sua senha <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="password"
                  className="controle-producao-modal-input"
                  style={modalInputStyle}
                  autoComplete="current-password"
                  placeholder="Digite sua senha de login"
                  value={resumePassword}
                  onChange={(e) => setResumePassword(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && resumePassword && !resumePending) {
                      handleConfirmResume();
                    }
                  }}
                />
              </div>
            </div>
            <div className="controle-producao-filters-modal-actions">
              <button className="controle-producao-btn-clear" onClick={() => { setResumeModalState(null); setResumePassword(''); }}>
                Cancelar
              </button>
              <button
                className="controle-producao-btn-apply"
                onClick={handleConfirmResume}
                disabled={!resumePassword || resumePending}
              >
                {resumePending ? 'Retomando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}


      {confirmUnrealizedModal.isOpen && (
        <ConfirmModal
          isOpen={confirmUnrealizedModal.isOpen}
          message={confirmUnrealizedModal.message}
          confirmLabel="Sim, tenho certeza"
          onConfirm={confirmUnrealizedModal.onConfirm}
          onCancel={() => setConfirmUnrealizedModal((prev) => ({ ...prev, isOpen: false }))}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo={isCollaboratorView ? 'Como Funciona — Acompanhamento de Veículos' : 'Como Funciona — Tempo e SLA'}
        secoes={helpSections}
      />
    </div>
  );
};

export default ControleProducao;

